import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addPositionMonitorSymbol,
  addTradeBlacklistSymbol,
  exitPosition,
  getPositionMonitorSymbols,
  getPositions,
  getTrades,
  exportClosedTradeAudit,
  getFunds,
  getHistoricalCandles,
  getOrders,
  getPortfolioSparklines,
  getTradeBlacklist,
  getAdvisorAnalyses,
  analyzeAdvisorPosition,
  clearAdvisorAutoMonitorScans,
  deleteAdvisorAutoMonitorScan,
  exportAdvisorAutoMonitorScans,
  exportAdvisorScoreHistory,
  getAdvisorAutoMonitorScans,
  getAdvisorScoreHistory,
  startAdvisorDeepScan,
  getAdvisorDeepScanStatus,
  removePositionMonitorSymbol,
  removeTradeBlacklistSymbol,
} from "../api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import CandleChart from "../components/CandleChart";
import StatCard from "../components/StatCard";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { downloadCsv, downloadJson } from "../utils/csv";
import { addDaysToYmd, formatIstTime, formatIstDateTime, periodRangeIst, currentPeriodValueIST, todayYmdIST } from "../utils/time";

const MODAL_CHART_RANGES = {
  "2H": { label: "2H", interval: "5minute", days: 1 },
  "1W": { label: "1W", interval: "30minute", days: 7 },
  "1M": { label: "1M", interval: "60minute", days: 30 },
  "6M": { label: "6M", interval: "day", days: 180 },
};

const CompactReason = ({ children, lines = 3, color = "#94a3b8" }) => {
  const text = String(children || "-");
  const clamped = Number.isFinite(Number(lines)) && Number(lines) > 0;
  return (
    <div
      title={text}
      style={{
        color,
        lineHeight: 1.4,
        minWidth: 0,
        overflow: clamped ? "hidden" : "visible",
        display: clamped ? "-webkit-box" : "block",
        WebkitLineClamp: clamped ? Number(lines) : "unset",
        WebkitBoxOrient: clamped ? "vertical" : "unset",
        overflowWrap: "break-word",
      }}
    >
      {text}
    </div>
  );
};

export default function Portfolio({ botStatus, onOpenSymbol }) {
  const isMobile = useMediaQuery("(max-width: 760px)");
  const [positions, setPositions] = useState({});
  const [trades, setTrades]       = useState([]);
  const [orders, setOrders]       = useState([]);
  const [funds, setFunds]         = useState(null);
  const [tab, setTab]             = useState("positions");
  const [exiting, setExiting]     = useState(null);
  const [error, setError]         = useState(null);
  const [notice, setNotice]       = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [exitingAll, setExitingAll] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [profitPeriod, setProfitPeriod] = useState("week");
  const [profitPeriodValue, setProfitPeriodValue] = useState(() => currentPeriodValueIST("week"));
  const [profitCustomToDate, setProfitCustomToDate] = useState(() => currentPeriodValueIST("day"));
  const [sparklines, setSparklines] = useState({});
  const [positionView, setPositionView] = useState("all");
  const [aiActionFilter, setAiActionFilter] = useState("ALL");
  const [positionDataFilter, setPositionDataFilter] = useState("ALL");
  const [monitorSymbols, setMonitorSymbols] = useState([]);
  const [blacklistSymbols, setBlacklistSymbols] = useState([]);
  const [chartModal, setChartModal] = useState(null);
  const [modalCandles, setModalCandles] = useState([]);
  const [modalChartMode, setModalChartMode] = useState("line");
  const [modalChartView, setModalChartView] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalSize, setModalSize] = useState({ width: 1100, height: 520 });
  const [advisorAnalyses, setAdvisorAnalyses] = useState({});
  const [advisorDeepScan, setAdvisorDeepScan] = useState(null);
  const [autoMonitorHistory, setAutoMonitorHistory] = useState({ scans: [], total: 0, updatedAt: null });
  const [autoMonitorLoading, setAutoMonitorLoading] = useState(false);
  const [analyzingSymbol, setAnalyzingSymbol] = useState(null);
  const [startingDeepScan, setStartingDeepScan] = useState(false);
  const [portfolioExpanded, setPortfolioExpanded] = useState(false);
  const [scoreChartModal, setScoreChartModal] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [scoreHistoryLoading, setScoreHistoryLoading] = useState(false);
  const [scoreHistoryError, setScoreHistoryError] = useState(null);
  const scoreHistoryRequestRef = useRef(0);
  const advisorEnabled = botStatus?.advisor?.enabled === true;
  const marketRegime = botStatus?.marketRegime?.latest || null;
  const marketRegimeState = String(marketRegime?.state || "UNAVAILABLE").toUpperCase();
  const marketRegimeColor = marketRegimeState === "NORMAL"
    ? "#22c55e"
    : marketRegimeState === "RISK_OFF"
      ? "#f59e0b"
      : marketRegimeState === "STRESS"
        ? "#ef4444"
        : "#64748b";
  const marketRegimeIndexes = Array.isArray(marketRegime?.indexes) ? marketRegime.indexes : [];

  const openScoreChart = useCallback(async ({ symbol, enteredAt, exitedAt = null }) => {
    if (!advisorEnabled) return;
    const enteredNumeric = Number(enteredAt);
    const enteredMs = Number.isFinite(enteredNumeric) && enteredNumeric > 0 ? enteredNumeric : Date.parse(enteredAt);
    if (!Number.isFinite(enteredMs) || enteredMs <= 0) {
      setError(`Cannot load ${symbol} score history because this trade has no valid entry timestamp.`);
      return;
    }
    const exitedNumeric = Number(exitedAt);
    const exitedMs = exitedAt == null
      ? null
      : Number.isFinite(exitedNumeric) && exitedNumeric > 0 ? exitedNumeric : Date.parse(exitedAt);
    const modal = { symbol: String(symbol || "").toUpperCase(), enteredAt: enteredMs, exitedAt: Number.isFinite(exitedMs) ? exitedMs : null };
    const requestId = scoreHistoryRequestRef.current + 1;
    scoreHistoryRequestRef.current = requestId;
    setScoreChartModal(modal);
    setScoreHistory([]);
    setScoreHistoryError(null);
    setScoreHistoryLoading(true);
    try {
      const payload = await getAdvisorScoreHistory({
        symbol: modal.symbol,
        enteredAt: modal.enteredAt,
        to: modal.exitedAt || undefined,
        limit: 20000,
      });
      if (scoreHistoryRequestRef.current === requestId) setScoreHistory(Array.isArray(payload?.points) ? payload.points : []);
    } catch (err) {
      if (scoreHistoryRequestRef.current === requestId) setScoreHistoryError(err?.message || "Failed to load score history");
    } finally {
      if (scoreHistoryRequestRef.current === requestId) setScoreHistoryLoading(false);
    }
  }, [advisorEnabled]);

  useEffect(() => {
    if (!portfolioExpanded) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPortfolioExpanded(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [portfolioExpanded]);

  useEffect(() => {
    if (!advisorEnabled && tab === "autoMonitor") setTab("positions");
    if (!advisorEnabled && ["ai", "risky"].includes(positionView)) setPositionView("all");
  }, [advisorEnabled, positionView, tab]);

  const loadPortfolio = useCallback(async ({ force = false } = {}) => {
    try {
      const pos = await getPositions(force && botStatus?.marketOpen ? { force: "true" } : undefined);
      const [tr, ord, f, monitor, blacklist, advisorPayload] = await Promise.all([
        getTrades({ excursions: tab === "trades" ? "true" : undefined }),
        getOrders(),
        getFunds(),
        getPositionMonitorSymbols(),
        getTradeBlacklist(),
        advisorEnabled ? getAdvisorAnalyses().catch((err) => ({ error: err?.message, analyses: {} })) : Promise.resolve({ analyses: {} }),
      ]);
      setPositions(pos || {});
      setTrades(Array.isArray(tr) ? tr : []);
      setOrders(Array.isArray(ord) ? ord : []);
      setFunds(f);
      setMonitorSymbols(Array.isArray(monitor?.symbols) ? monitor.symbols : []);
      setBlacklistSymbols(Array.isArray(blacklist?.symbols) ? blacklist.symbols : []);
      setAdvisorAnalyses(advisorPayload?.analyses || {});
      setLastLoadedAt(new Date());
      setError(null);
    } catch (err) { setError(err?.message || "Failed to load portfolio"); }
  }, [advisorEnabled, botStatus?.marketOpen, tab]);

  const loadAutoMonitorHistory = useCallback(async () => {
    setAutoMonitorLoading(true);
    try {
      const payload = await getAdvisorAutoMonitorScans({ limit: 1000 });
      setAutoMonitorHistory({
        scans: Array.isArray(payload?.scans) ? payload.scans : [],
        total: Number(payload?.total || 0),
        updatedAt: payload?.updatedAt || null,
      });
    } catch (err) {
      setError(err?.message || "Failed to load auto-monitor scans");
    } finally {
      setAutoMonitorLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPortfolio();
    if (!botStatus?.marketOpen) return undefined;
    const t = setInterval(() => loadPortfolio(), 3000);
    return () => clearInterval(t);
  }, [botStatus?.marketOpen, loadPortfolio]);

  useEffect(() => {
    const live = new Set(Object.keys(positions));
    setMonitorSymbols((prev) => {
      const next = prev.filter((symbol) => live.has(symbol));
      return next.length === prev.length ? prev : next;
    });
  }, [positions]);

  useEffect(() => {
    if (!advisorEnabled) {
      setAdvisorDeepScan(null);
      return undefined;
    }
    let cancelled = false;
    const loadDeepScanStatus = async () => {
      try {
        const status = await getAdvisorDeepScanStatus();
        if (cancelled) return;
        setAdvisorDeepScan(status || null);
        if (status?.analyses) setAdvisorAnalyses(status.analyses);
      } catch (_) {}
    };
    loadDeepScanStatus();
    const t = setInterval(loadDeepScanStatus, advisorDeepScan?.running ? 3000 : 12000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [advisorDeepScan?.running, advisorEnabled]);

  useEffect(() => {
    const updateModalSize = () => {
      if (typeof window === "undefined") return;
      setModalSize({
        width: Math.max(720, Math.floor(window.innerWidth * (isMobile ? 0.92 : 0.76))),
        height: Math.max(320, Math.floor(window.innerHeight * (isMobile ? 0.58 : 0.56))),
      });
    };
    updateModalSize();
    window.addEventListener("resize", updateModalSize);
    return () => window.removeEventListener("resize", updateModalSize);
  }, [isMobile]);

  useEffect(() => {
    if (!advisorEnabled) {
      setAdvisorAnalyses({});
      return undefined;
    }
    let cancelled = false;
    const loadAnalyses = async () => {
      try {
        const payload = await getAdvisorAnalyses();
        if (!cancelled && payload?.analyses) setAdvisorAnalyses(payload.analyses);
      } catch (_) {}
    };
    loadAnalyses();
    const t = setInterval(loadAnalyses, botStatus?.marketOpen ? 10000 : 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [advisorEnabled, botStatus?.marketOpen]);

  // Build equity curve from trades
  const initialCapital = botStatus?.capital || 100000;
  const pctOf = (amount, base) => {
    const n = Number(amount);
    const b = Number(base);
    return Number.isFinite(n) && Number.isFinite(b) && b > 0 ? (n / b) * 100 : null;
  };
  const tradeBuyValue = (trade) => {
    const explicit = Number(trade?.buyValue);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const entry = Number(trade?.entryPrice ?? trade?.buyPrice);
    const qty = Number(trade?.qty);
    return Number.isFinite(entry) && Number.isFinite(qty) && entry > 0 && qty > 0 ? entry * qty : null;
  };
  const tradeNetProfitPct = (trade) => {
    const explicit = Number(trade?.netProfitPct ?? trade?.profitPct);
    if (Number.isFinite(explicit)) return explicit;
    return pctOf(trade?.pnl, tradeBuyValue(trade));
  };
  const tradeGrossProfitPct = (trade) => {
    const explicit = Number(trade?.grossProfitPct);
    if (Number.isFinite(explicit)) return explicit;
    return pctOf(trade?.grossPnl ?? trade?.pnl, tradeBuyValue(trade));
  };
  const equityCurve = trades.reduce((acc, t, i) => {
    const prev = acc[acc.length - 1]?.value || initialCapital;
    acc.push({ i: i + 1, value: prev + (t.pnl || 0), label: t.symbol });
    return acc;
  }, [{ i: 0, value: initialCapital, label: "Start" }]);

  const monitorSet = useMemo(() => new Set(monitorSymbols), [monitorSymbols]);
  const blacklistSet = useMemo(() => new Set(blacklistSymbols), [blacklistSymbols]);
  const openRows    = Object.entries(positions);
  const aiActionForSymbol = (symbol) => String(advisorAnalyses[String(symbol || "").toUpperCase()]?.action || "NO_ANALYSIS").toUpperCase();
  const aiRiskBucketForSymbol = (symbol) => String(advisorAnalyses[String(symbol || "").toUpperCase()]?.riskBucket || "").toUpperCase();
  const riskyAiActions = new Set(["HIGH_RISK", "TIGHTEN_STOP", "PARTIAL_EXIT", "EXIT"]);
  const isRiskyOpenRow = ([symbol]) => aiRiskBucketForSymbol(symbol) === "RISKY" || riskyAiActions.has(aiActionForSymbol(symbol));
  const isMonitorOpenRow = ([symbol]) => aiRiskBucketForSymbol(symbol) === "MONITOR" || monitorSet.has(symbol) || aiActionForSymbol(symbol) === "WATCH";
  const riskyOpenRows = openRows.filter(isRiskyOpenRow);
  const monitorOpenRows = openRows.filter((row) => !isRiskyOpenRow(row) && isMonitorOpenRow(row));
  const normalOpenRows = openRows.filter((row) => !isRiskyOpenRow(row) && !isMonitorOpenRow(row));
  const allOpenRows = openRows;
  const aiOpenRows = openRows;
  const baseVisibleOpenRows =
    positionView === "all" ? allOpenRows
      : positionView === "monitor" ? monitorOpenRows
      : positionView === "risky" ? riskyOpenRows
        : positionView === "ai" ? aiOpenRows
          : normalOpenRows;
  const aiActionFilterDefs = [
    ["ALL", "All AI"],
    ["NO_ANALYSIS", "No AI"],
    ["HOLD", "Hold"],
    ["WATCH", "Watch"],
    ["TIGHTEN_STOP", "Tighten"],
    ["HIGH_RISK", "High Risk"],
    ["PARTIAL_EXIT", "Partial"],
    ["EXIT", "Exit"],
    ["AI_UNAVAILABLE", "AI Unavailable"],
  ];
  const aiActionCounts = Object.fromEntries(aiActionFilterDefs.map(([id]) => [
    id,
    id === "ALL" ? baseVisibleOpenRows.length : baseVisibleOpenRows.filter(([symbol]) => aiActionForSymbol(symbol) === id).length,
  ]));
  const aiFilteredOpenRows = aiActionFilter === "ALL"
    ? baseVisibleOpenRows
    : positionView === "ai"
      ? baseVisibleOpenRows.filter(([symbol]) => aiActionForSymbol(symbol) === aiActionFilter)
      : baseVisibleOpenRows;
  const pnlPctForPosition = (pos) => {
    const ltp = Number(pos?.currentPrice || pos?.entry);
    const entry = Number(pos?.entry);
    if (!Number.isFinite(ltp) || !Number.isFinite(entry) || entry === 0) return 0;
    return ((ltp - entry) / entry) * 100;
  };
  const heldDaysForPosition = (pos) => {
    const days = Number(pos?.daysHeld);
    if (Number.isFinite(days) && days > 0) return days;
    const ms = Number(pos?.holdingMs);
    if (Number.isFinite(ms) && ms >= 0) return ms / 86400000;
    return 0;
  };
  const givebackPctForPosition = (pos) => {
    const maxGain = Number(pos?.maxGainPct);
    if (!Number.isFinite(maxGain)) return 0;
    return maxGain - pnlPctForPosition(pos);
  };
  const trailingStatusForPosition = (pos) => String(pos?.trailingStopStatus || (pos?.trailingStopActive ? "ACTIVE" : pos?.trailingStopEnabled ? "WAITING" : "OFF")).toUpperCase();
  const positionDataFilterDefs = [
    ["ALL", "All data", () => true],
    ["PROFIT_2", "Profit >2%", ([, pos]) => pnlPctForPosition(pos) > 2],
    ["PROFIT_5", "Profit >5%", ([, pos]) => pnlPctForPosition(pos) > 5],
    ["LOSS_2", "Loss >2%", ([, pos]) => pnlPctForPosition(pos) < -2],
    ["GIVEBACK_2", "Giveback >2%", ([, pos]) => givebackPctForPosition(pos) > 2],
    ["HELD_3", "Held >3d", ([, pos]) => heldDaysForPosition(pos) > 3],
    ["HELD_7", "Held >7d", ([, pos]) => heldDaysForPosition(pos) > 7],
    ["TRAILING_ACTIVE", "Trailing active", ([, pos]) => trailingStatusForPosition(pos) === "ACTIVE"],
    ["TRAILING_WAITING", "Trailing waiting", ([, pos]) => trailingStatusForPosition(pos) === "WAITING"],
  ];
  const activePositionDataFilter = positionDataFilterDefs.find(([id]) => id === positionDataFilter)?.[2] || (() => true);
  const positionDataFilterCounts = Object.fromEntries(positionDataFilterDefs.map(([id, , predicate]) => [
    id,
    id === "ALL" ? aiFilteredOpenRows.length : aiFilteredOpenRows.filter(predicate).length,
  ]));
  const visibleOpenRows = positionDataFilter === "ALL"
    ? aiFilteredOpenRows
    : aiFilteredOpenRows.filter(activePositionDataFilter);
  const sparklineItems = useMemo(() => {
    if (tab === "positions") {
      return Object.entries(positions).map(([symbol, pos]) => ({ symbol, instrumentKey: pos?.instrumentKey || "" }));
    }
    if (tab === "trades") {
      const seen = new Set();
      return [...trades].reverse()
        .map((trade) => ({ symbol: String(trade?.symbol || "").toUpperCase(), instrumentKey: trade?.instrumentKey || "" }))
        .filter((item) => {
          if (!item.symbol && !item.instrumentKey) return false;
          const key = item.instrumentKey || item.symbol;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 60);
    }
    return [];
  }, [positions, tab, trades]);
  const sparklineRequestKey = sparklineItems.map((item) => `${item.instrumentKey || ""}:${item.symbol || ""}`).join("|");
  const openPositionExportRows = openRows.map(([symbol, position]) => ({ symbol, ...position }));
  const closedTradeExportRows = trades.map((trade) => ({
    ...trade,
    grossProfitPct: tradeGrossProfitPct(trade),
    netProfitPct: tradeNetProfitPct(trade),
    profitPct: tradeNetProfitPct(trade),
  }));
  const latestClosedTradeExportRows = [...closedTradeExportRows]
    .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
    .slice(0, 20);
  const openNetPnL  = openRows.reduce((s, [, p]) => s + Number(p.netPnl ?? ((p.currentPrice || p.entry) - p.entry) * p.qty), 0);
  const closedNetPnL = trades.reduce((s, t) => s + (t.pnl || 0), 0);
  const usedCapital = funds?.used ?? openRows.reduce((s, [, p]) => s + Number(p.investedValue || ((p.entry || 0) * (p.qty || 0))), 0);
  const availableCapital = funds?.available ?? ((botStatus?.capital || 100000) - usedCapital + closedNetPnL);
  const openCount   = Object.keys(positions).length;
  const allOpenCount = allOpenRows.length;
  const normalOpenCount = normalOpenRows.length;
  const monitorOpenCount = monitorOpenRows.length;
  const riskyOpenCount = riskyOpenRows.length;
  const aiOpenCount = aiOpenRows.length;
  const positionViewLabel = positionView === "all" ? "all"
    : positionView === "monitor" ? "monitor"
    : positionView === "risky" ? "risky"
      : positionView === "ai" ? "AI"
        : "normal";
  const exitAllLabel = positionView === "all" ? "Positions" : positionViewLabel;
  const orderCount  = orders.length;
  const wins        = trades.filter(t => Number(t.pnl || 0) >= 0);
  const winRate     = trades.length ? (wins.length / trades.length * 100).toFixed(1) : "—";
  const avgWin      = wins.length ? (wins.reduce((s, t) => s + t.pnl, 0) / wins.length).toFixed(0) : "0";
  const losses      = trades.filter(t => (t.pnl || 0) < 0);
  const avgLoss     = losses.length ? (losses.reduce((s, t) => s + t.pnl, 0) / losses.length).toFixed(0) : "0";
  const autoMonitorScans = Array.isArray(autoMonitorHistory.scans) ? autoMonitorHistory.scans : [];
  const autoMonitorRows = autoMonitorScans.flatMap((scan) => {
    const rows = Array.isArray(scan.symbols) ? scan.symbols : [];
    if (!rows.length) {
      return [{
        scan,
        symbol: "",
        status: scan.status === "running" ? "RUNNING" : scan.status === "skipped" ? "SKIPPED" : scan.status === "error" ? "ERROR" : "NO_SYMBOL_ROWS",
        updatedByScan: false,
        decision: "-",
        action: "-",
        riskBucket: "-",
        urgency: null,
        confidence: null,
        monitorMove: "-",
        summary: "",
        error: scan.aiError || scan.error || scan.skipped || "",
      }];
    }
    return rows.map((row) => ({ scan, ...row }));
  });
  const activeExport = tab === "positions"
    ? { label: "Download Positions CSV", rows: openPositionExportRows, file: "open-positions" }
    : tab === "orders"
      ? { label: "Download Orders CSV", rows: orders, file: "orders" }
      : tab === "trades"
        ? { label: "Download Closed Trades CSV", rows: closedTradeExportRows, file: "closed-trades" }
        : tab === "autoMonitor"
          ? {
            label: "Export Auto Monitor CSV",
            rows: autoMonitorRows.map((row) => ({
              scanId: row.scan?.id,
              startedAt: row.scan?.startedAt,
              finishedAt: row.scan?.finishedAt,
              scanStatus: row.scan?.status,
              scanReason: row.scan?.reason,
              openPositions: row.scan?.openPositions,
              analyzed: row.scan?.analyzed,
              jsonRepaired: row.scan?.jsonRepaired,
              provider: row.provider || row.scan?.provider,
              model: row.model || row.scan?.model,
              symbol: row.symbol,
              symbolStatus: row.status,
              updatedByScan: row.updatedByScan,
              decision: row.decision,
              action: row.action,
              riskBucket: row.riskBucket,
              urgency: row.urgency,
              confidence: row.confidence,
              monitorMove: row.monitorMove,
              suggestedStopLoss: row.suggestedStopLoss,
              suggestedTargetPrice: row.suggestedTargetPrice,
              riskUpdated: row.riskUpdated,
              summary: row.summary,
              error: row.error,
            })),
            file: "auto-monitor-scan-history",
          }
          : null;

  useEffect(() => {
    if (!sparklineItems.length) return undefined;
    let cancelled = false;
    getPortfolioSparklines({ items: sparklineItems, interval: "5minute", days: 7 })
      .then((data) => {
        if (cancelled) return;
        const next = {};
        for (const row of data?.sparklines || []) {
          const key = row?.instrumentKey || row?.symbol;
          if (key) next[key] = row;
          if (row?.symbol) next[row.symbol] = row;
        }
        setSparklines((prev) => ({ ...prev, ...next }));
      })
      .catch((err) => {
        if (cancelled) return;
        const failedAt = Date.now();
        const next = {};
        for (const item of sparklineItems) {
          const key = item.instrumentKey || item.symbol;
          if (key) next[key] = { points: [], source: "error", error: err?.message || "sparkline request failed", updatedAt: failedAt };
          if (item.symbol) next[item.symbol] = next[key];
        }
        setSparklines((prev) => ({ ...prev, ...next }));
      });
    return () => { cancelled = true; };
  }, [sparklineRequestKey]);

  const currentWeekValue = currentPeriodValueIST("week");
  const currentMonthValue = currentPeriodValueIST("month");
  const profitRangeValue = useMemo(() => (
    profitPeriod === "custom"
      ? { fromDate: profitPeriodValue, toDate: profitCustomToDate }
      : profitPeriodValue
  ), [profitPeriod, profitPeriodValue, profitCustomToDate]);
  const selectedRange = periodRangeIst(profitPeriod, profitRangeValue);
  const realizedProfitForRange = (range) => {
    return trades.reduce((sum, t) => {
      const time = new Date(t.timestamp || t.exitedAt || 0).getTime();
      return time >= range.start && time < range.end ? sum + Number(t.pnl || 0) : sum;
    }, 0);
  };
  const selectedPeriodProfit = realizedProfitForRange(selectedRange);
  const weekProfit = realizedProfitForRange(periodRangeIst("week", currentWeekValue));
  const monthProfit = realizedProfitForRange(periodRangeIst("month", currentMonthValue));
  const selectPositionView = (view) => {
    setPositionView(view);
    setAiActionFilter("ALL");
    setPositionDataFilter("ALL");
  };
  const handleProfitPeriodChange = (period) => {
    setProfitPeriod(period);
    setProfitPeriodValue(currentPeriodValueIST(period));
    if (period === "custom") setProfitCustomToDate(currentPeriodValueIST("day"));
  };
  const fmtPct = (value) => {
    if (value == null || value === "") return "—";
    const n = Number(value);
    return Number.isFinite(n) ? `${n >= 0 ? "+" : ""}${n.toFixed(2)}%` : "—";
  };
  const fmtPrice = (value) => {
    if (value == null || value === "") return "—";
    const n = Number(value);
    return Number.isFinite(n) ? `Rs ${n.toFixed(2)}` : "—";
  };
  const pricePctFromEntry = (price, entry) => {
    const p = Number(price);
    const e = Number(entry);
    return Number.isFinite(p) && Number.isFinite(e) && e > 0 ? ((p - e) / e) * 100 : null;
  };
  const fmtPriceWithEntryPct = (price, entry) => {
    const label = fmtPrice(price);
    const pct = pricePctFromEntry(price, entry);
    return pct == null ? label : `${label} (${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%)`;
  };
  const aiManagedLabel = (kind, pos) => {
    const at = kind === "stop" ? pos?.aiStopLossUpdatedAt : pos?.aiTakeProfitUpdatedAt;
    if (!at) return null;
    const sourceType = String(pos?.aiStopTargetLastDecision?.analysisType || "").replace(/_/g, " ").toUpperCase();
    const source = sourceType ? ` from ${sourceType}` : "";
    return (
      <div style={{ marginTop: 3, color: "#38bdf8", fontSize: 9, fontWeight: 800, whiteSpace: "nowrap" }}>
        AI set {formatIstDateTime(at)}{source}
      </div>
    );
  };
  const brokerManagedLabel = (kind, pos) => {
    if (pos?.paper) return null;
    const isStop = kind === "stop";
    const orderId = isStop ? pos?.brokerStopGttOrderId : pos?.brokerTargetGttOrderId;
    const status = isStop ? pos?.brokerStopOrderStatus : pos?.brokerTargetOrderStatus;
    const trigger = isStop ? pos?.brokerStopTriggerPrice : pos?.brokerTargetTriggerPrice;
    if (!orderId && !status) return null;
    const healthy = String(status || "").toUpperCase() === "ACTIVE";
    return (
      <div title={orderId || ""} style={{ marginTop: 3, color: healthy ? "#22c55e" : "#f59e0b", fontSize: 9, fontWeight: 800, whiteSpace: "nowrap" }}>
        UPSTOX GTT {status || "PENDING"}{Number(trigger) > 0 ? ` @ Rs ${Number(trigger).toFixed(2)}` : ""}
      </div>
    );
  };
  const fmtDuration = (ms) => {
    const n = Number(ms);
    if (!Number.isFinite(n) || n < 0) return "—";
    const mins = Math.floor(n / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
  };

  const fmtHeldDays = (value, fallbackMs) => {
    const days = Number(value);
    if (Number.isFinite(days) && days > 0) return `${Math.ceil(days)}d`;
    const ms = Number(fallbackMs);
    if (Number.isFinite(ms) && ms >= 0) return `${Math.max(1, Math.ceil(ms / 86400000))}d`;
    return "-";
  };
  const fmtMinMaxCell = (pct, price) => (
    <div>
      <div>{fmtPct(pct)}</div>
      <div style={{ color: "#64748b", fontSize: 10 }}>{fmtPrice(price)}</div>
    </div>
  );
  const fmtAmountQtyCell = (amount, qty) => (
    <div>
      <div>{fmtPrice(amount)}</div>
      <div style={{ color: "#64748b", fontSize: 10 }}>{Number(qty || 0).toLocaleString("en-IN")} share{Number(qty || 0) === 1 ? "" : "s"}</div>
    </div>
  );

  const fmtTrailingStatus = (row) => {
    const status = row?.trailingStopStatus || (row?.trailingStopActive ? "ACTIVE" : row?.trailingStopEnabled ? "WAITING" : "OFF");
    const color = status === "ACTIVE" ? "#22c55e" : status === "WAITING" ? "#f59e0b" : "#64748b";
    return (
      <div style={{ fontSize: 10, lineHeight: 1.45, color }}>
        <div style={{ fontWeight: 800 }}>{status}</div>
        <div style={{ color: "#94a3b8" }}>{fmtPrice(row?.trailingStopPrice ?? row?.stopLoss)}</div>
        <div style={{ color: "#64748b", whiteSpace: "nowrap" }}>
          {Number(row?.trailingStopActivationPct ?? 0).toFixed(0)}% / {Number(row?.trailingStopDistancePct ?? 0).toFixed(0)}%
        </div>
      </div>
    );
  };

  const closeChartModal = () => {
    setChartModal(null);
    setModalCandles([]);
    setModalChartView(null);
    setModalError(null);
  };

  const openChartModal = useCallback(async ({ symbol, instrumentKey, range = "1W", trade = null } = {}) => {
    const sym = String(symbol || "").trim().toUpperCase();
    if (!sym) return;
    const cfg = MODAL_CHART_RANGES[range] || MODAL_CHART_RANGES["1W"];
    const toDate = todayYmdIST();
    setChartModal({ symbol: sym, instrumentKey: instrumentKey || "", range, trade });
    setModalCandles([]);
    setModalChartView(null);
    setModalError(null);
    setModalLoading(true);
    try {
      const data = await getHistoricalCandles({
        symbol: sym,
        instrumentKey: instrumentKey || undefined,
        interval: cfg.interval,
        toDate,
        fromDate: addDaysToYmd(toDate, -cfg.days),
      });
      const candles = Array.isArray(data?.candles) ? data.candles : [];
      setModalCandles(candles);
      if (candles.length <= 5) setModalError("No chart candles available for this symbol/range.");
    } catch (err) {
      setModalError(err?.message || "Failed to load chart candles.");
    } finally {
      setModalLoading(false);
    }
  }, []);

  const MiniSparkline = ({ row, label }) => {
    const points = Array.isArray(row?.points) ? row.points : [];
    const w = 96;
    const h = 24;
    if (points.length < 2) {
      return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", background: "#07111f", border: "1px solid #1e293b", borderRadius: 5 }} aria-label={`${label || "price"} trend loading`}>
          <path d={`M4 ${Math.round(h / 2)} L${w - 4} ${Math.round(h / 2)}`} fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="3 3" strokeLinecap="round" />
          {label && <text x="5" y="9" fill="#64748b" fontSize="7" fontFamily="monospace">{label}</text>}
        </svg>
      );
    }
    const closes = points.map((point) => Number(point.close)).filter(Number.isFinite);
    const min = Math.min(...closes);
    const max = Math.max(...closes);
    const span = max - min || 1;
    const path = points.map((point, index) => {
      const x = 4 + (points.length === 1 ? 0 : (index / (points.length - 1)) * (w - 8));
      const y = h - ((Number(point.close) - min) / span) * (h - 8) - 4;
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    const first = Number(points[0]?.close);
    const last = Number(points[points.length - 1]?.close);
    const color = last >= first ? "#22c55e" : "#ef4444";
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", background: "#07111f", border: "1px solid #1e293b", borderRadius: 5 }} aria-label={`${label || "price"} trend`}>
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {label && <text x="5" y="9" fill="#64748b" fontSize="7" fontFamily="monospace">{label}</text>}
      </svg>
    );
  };

  const SymbolCell = ({ symbol, instrumentKey, onClick, onOpenChart, onOpenScore, onAnalyze, analyzing }) => {
    const chart = sparklines[instrumentKey] || sparklines[symbol];
    const shortTrend = chart?.trends?.short || chart;
    const weekTrend = chart?.trends?.week || null;
    const hasShortTrend = Array.isArray(shortTrend?.points) && shortTrend.points.length >= 2;
    const hasWeekTrend = Array.isArray(weekTrend?.points) && weekTrend.points.length >= 2;
    const hasTrend = hasShortTrend || hasWeekTrend;
    const shortError = chart?.error ? String(chart.error).replace(/^Upstox\s*/i, "").slice(0, 28) : "";
    const sourceLabel = !chart
      ? "loading trend"
      : hasTrend
        ? `${hasShortTrend ? "2h" : ""}${hasShortTrend && hasWeekTrend ? " + " : ""}${hasWeekTrend ? "1w" : ""}`
        : shortError || "no trend data";
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 5, minWidth: 116 }}>
        <button
          type="button"
          onClick={() => onOpenChart?.("2H")}
          style={{ background: "transparent", border: "none", padding: 0, cursor: "zoom-in" }}
          title="Open 2 hour chart"
        >
          <MiniSparkline row={shortTrend} label="2h" />
        </button>
        <div onClick={onClick} style={{ fontWeight: 700, color: "#38bdf8", fontFamily: "'Syne',sans-serif", textDecoration: "underline", lineHeight: 1.1, cursor: "pointer" }}>{symbol}</div>
        {onAnalyze && (
          <button
            type="button"
            disabled={analyzing}
            onClick={onAnalyze}
            style={{
              background: analyzing ? "#111827" : "#052e1a",
              border: `1px solid ${analyzing ? "#334155" : "#22c55e66"}`,
              color: analyzing ? "#64748b" : "#22c55e",
              borderRadius: 6,
              padding: "5px 8px",
              fontSize: 10,
              fontWeight: 800,
              cursor: analyzing ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {analyzing ? "Analyzing..." : "Analyze"}
          </button>
        )}
        {onOpenScore && (
          <button
            type="button"
            onClick={onOpenScore}
            style={{ background: "#07111f", border: "1px solid #a78bfa66", color: "#a78bfa", borderRadius: 6, padding: "5px 8px", fontSize: 10, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}
            title="View scanner score history for this trade"
          >
            Score chart
          </button>
        )}
        <button
          type="button"
          onClick={() => onOpenChart?.("1W")}
          style={{ background: "transparent", border: "none", padding: 0, cursor: "zoom-in" }}
          title="Open 1 week chart"
        >
          <MiniSparkline row={weekTrend} label="1w" />
        </button>
        <div title={chart?.error || ""} style={{ fontSize: 9, color: hasTrend ? "#475569" : "#f59e0b", whiteSpace: "nowrap", lineHeight: 1 }}>
          {sourceLabel}
        </div>
      </div>
    );
  };

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setTab(id)} style={{
      background: "none", border: "none",
      borderBottom: `2px solid ${tab === id ? "#22c55e" : "transparent"}`,
      color: tab === id ? "#22c55e" : "#475569",
      padding: "8px 16px", cursor: "pointer",
      fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: tab === id ? 600 : 400,
    }}>
      {label}
    </button>
  );

  const loadNow = async () => {
    setRefreshing(true);
    await loadPortfolio({ force: true });
    setRefreshing(false);
  };

  const handleManualExit = async (sym) => {
    setExiting(sym);
    setError(null);
    try {
      await exitPosition(sym, { reason: "MANUAL_EXIT" });
      await loadNow();
    } catch (err) {
      setError(err?.message || "Manual exit failed");
    }
    setExiting(null);
  };

  const moveToMonitor = async (symbol) => {
    const sym = String(symbol || "").toUpperCase();
    if (!sym) return;
    setError(null);
    try {
      const result = await addPositionMonitorSymbol(sym);
      setMonitorSymbols(Array.isArray(result?.symbols) ? result.symbols : []);
    } catch (err) {
      setError(err?.message || `Failed to move ${sym} to monitor`);
    }
  };

  const moveToNormal = async (symbol) => {
    const sym = String(symbol || "").toUpperCase();
    if (!sym) return;
    setError(null);
    try {
      const result = await removePositionMonitorSymbol(sym);
      setMonitorSymbols(Array.isArray(result?.symbols) ? result.symbols : []);
    } catch (err) {
      setError(err?.message || `Failed to move ${sym} to normal`);
    }
  };

  const toggleBlacklist = async (symbol) => {
    const sym = String(symbol || "").toUpperCase();
    if (!sym) return;
    setError(null);
    setNotice(null);
    try {
      const adding = !blacklistSet.has(sym);
      const result = blacklistSet.has(sym)
        ? await removeTradeBlacklistSymbol(sym)
        : await addTradeBlacklistSymbol(sym);
      setBlacklistSymbols(Array.isArray(result?.symbols) ? result.symbols : []);
      setNotice(`${sym} ${adding ? "added to" : "removed from"} blacklist`);
    } catch (err) {
      setError(err?.message || `Failed to update blacklist for ${sym}`);
    }
  };

  const handleExitAll = async () => {
    const symbols = visibleOpenRows.map(([symbol]) => symbol);
    if (!symbols.length) return;
    const label = positionViewLabel;
    const ok = window.confirm(`Exit all ${symbols.length} ${label} open position(s)?`);
    if (!ok) return;
    setExitingAll(true);
    setError(null);
    const failed = [];
    try {
      for (const symbol of symbols) {
        setExiting(symbol);
        try {
          await exitPosition(symbol, { reason: `MANUAL_EXIT_ALL_${String(positionView).toUpperCase()}` });
        } catch (err) {
          failed.push(`${symbol}: ${err?.message || "exit failed"}`);
        }
      }
      setExiting(null);
      await loadNow();
    } finally {
      setExiting(null);
      setExitingAll(false);
    }
    if (failed.length) {
      setError(`Exit all completed with ${failed.length} failure(s): ${failed.slice(0, 3).join(" | ")}${failed.length > 3 ? " ..." : ""}`);
    }
  };

  const analyzePosition = async (symbol) => {
    const sym = String(symbol || "").toUpperCase();
    if (!sym) return;
    setAnalyzingSymbol(sym);
    setError(null);
    try {
      const result = await analyzeAdvisorPosition(sym);
      if (result?.analyses) setAdvisorAnalyses(result.analyses);
      else if (result?.analysis) setAdvisorAnalyses((prev) => ({ ...prev, [sym]: result.analysis }));
    } catch (err) {
      setError(err?.message || `AI analysis failed for ${sym}`);
    } finally {
      setAnalyzingSymbol(null);
    }
  };

  const startDeepScanAll = async (mode = "sequential") => {
    setStartingDeepScan(true);
    setError(null);
    try {
      const status = await startAdvisorDeepScan(mode);
      setAdvisorDeepScan(status || null);
      if (status?.analyses) setAdvisorAnalyses(status.analyses);
    } catch (err) {
      setError(err?.message || "Failed to start AI deep scan");
    } finally {
      setStartingDeepScan(false);
    }
  };

  const exportAutoMonitorHistory = async () => {
    try {
      const payload = await exportAdvisorAutoMonitorScans({ limit: 1000 });
      const ok = downloadCsv("auto-monitor-scan-history", payload?.rows || []);
      if (!ok) setError("No auto-monitor rows available to export");
    } catch (err) {
      setError(err?.message || "Failed to export auto-monitor scans");
    }
  };

  const exportPortfolioAudit = async ({ scope, latest20 = false } = {}) => {
    const baseRows = scope === "open"
      ? openPositionExportRows
      : latest20 ? latestClosedTradeExportRows : closedTradeExportRows;
    const baseName = scope === "open" ? "open-positions" : latest20 ? "closed-trades-last-20" : "closed-trades";
    if (scope === "closed") {
      try {
        const payload = await exportClosedTradeAudit(latest20 ? { limit: 20 } : undefined);
        downloadJson(`${baseName}-audit`, payload, { pretty: false });
        setNotice(`Downloaded ${payload?.tradeCount || 0} closed trades with ${payload?.tradesWithScoreHistory || 0} score histories and ${payload?.tradesWithAutoMonitorHistory || 0} auto-monitor histories.`);
      } catch (err) {
        downloadCsv(baseName, baseRows);
        setNotice(`Downloaded the base closed-trades CSV because the structured audit export failed: ${err?.message || "audit export failed"}`);
      }
      return;
    }
    if (!advisorEnabled) {
      downloadCsv(baseName, baseRows);
      return;
    }
    try {
      const [scoresPayload, monitorPayload] = await Promise.all([
        exportAdvisorScoreHistory({ limit: 20000 }),
        exportAdvisorAutoMonitorScans({ limit: 1000 }),
      ]);
      const toTimestamp = (value, fallback = 0) => {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0) return numeric;
        const parsed = Date.parse(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
      };
      const selectedTrades = (scope === "open"
        ? Object.entries(positions).map(([symbol, pos]) => ({ symbol, enteredAt: toTimestamp(pos.enteredAt), exitedAt: Infinity }))
        : baseRows.map((trade) => ({
          symbol: String(trade.symbol || "").toUpperCase(),
          enteredAt: toTimestamp(trade.enteredAt),
          exitedAt: toTimestamp(trade.timestamp || trade.exitedAt, Infinity),
        }))).filter((trade) => trade.symbol && trade.enteredAt > 0);
      const selectedKeys = new Set(selectedTrades.map((trade) => `${trade.symbol}:${trade.enteredAt}`));
      const scoreRows = (scoresPayload?.rows || [])
        .filter((point) => selectedKeys.has(point.tradeKey || `${point.symbol}:${Number(point.enteredAt) || 0}`))
        .map((point) => ({ recordType: "SCORE_POINT", ...point }));
      const monitorRows = (monitorPayload?.rows || []).filter((row) => {
        if (!row.symbol) return false;
        const at = new Date(row.finishedAt || row.analyzedAt || row.startedAt || 0).getTime();
        return selectedTrades.some((trade) => trade.symbol === String(row.symbol).toUpperCase() && at >= trade.enteredAt && at <= trade.exitedAt);
      }).map((row) => ({ recordType: "AUTO_MONITOR", ...row }));
      downloadCsv(baseName, [
        ...baseRows.map((row) => ({ recordType: scope === "open" ? "OPEN_POSITION" : "CLOSED_TRADE", ...row })),
        ...scoreRows,
        ...monitorRows,
      ]);
    } catch (err) {
      downloadCsv(baseName, baseRows);
      setNotice(`Downloaded the base ${scope === "open" ? "positions" : "closed trades"} CSV, but AI audit rows could not be added: ${err?.message || "supplemental export failed"}`);
    }
  };

  const clearAutoMonitorHistory = async () => {
    if (!window.confirm("Clear all stored auto-monitor scan history?")) return;
    try {
      await clearAdvisorAutoMonitorScans();
      await loadAutoMonitorHistory();
    } catch (err) {
      setError(err?.message || "Failed to clear auto-monitor scans");
    }
  };

  const deleteAutoMonitorRun = async (id) => {
    try {
      await deleteAdvisorAutoMonitorScan(id);
      await loadAutoMonitorHistory();
    } catch (err) {
      setError(err?.message || "Failed to delete auto-monitor scan");
    }
  };

  const advisorProviderLabel = botStatus?.advisor?.provider === "cloudflare_workers_ai"
    ? "Cloudflare Workers AI"
    : botStatus?.advisor?.provider === "gemini"
      ? "Gemini"
    : botStatus?.advisor?.provider || "AI";
  const advisorModelLabel = botStatus?.advisor?.model || "";
  const advisorBulkEnabled = ["cloudflare_workers_ai", "gemini"].includes(botStatus?.advisor?.provider);
  const advisorAutoMonitor = botStatus?.advisor?.autoMonitor || null;
  const advisorAutoLastRun = advisorAutoMonitor?.lastRun || null;
  const advisorAutoAnalyzed = Number(advisorAutoLastRun?.aiScan?.analyzed || 0);
  const advisorAutoStatusColor = advisorAutoMonitor?.running
    ? "#38bdf8"
    : advisorAutoLastRun?.status === "error" || advisorAutoLastRun?.aiError
      ? "#f87171"
      : advisorAutoLastRun?.finishedAt
        ? "#22c55e"
        : "#64748b";
  const advisorAutoStatusText = advisorAutoMonitor?.running
    ? "Auto AI running"
    : advisorAutoLastRun?.finishedAt
      ? `Auto AI ${formatIstTime(advisorAutoLastRun.finishedAt)} - ${advisorAutoAnalyzed}/${advisorAutoLastRun.openPositions || 0}`
      : "Auto AI waiting";
  const advisorAutoTitle = advisorAutoLastRun
    ? [
      `status: ${advisorAutoLastRun.status || "unknown"}`,
      advisorAutoLastRun.reason ? `reason: ${advisorAutoLastRun.reason}` : null,
      advisorAutoLastRun.aiError ? `aiError: ${advisorAutoLastRun.aiError}` : null,
      advisorAutoLastRun.aiScan?.provider ? `provider: ${advisorAutoLastRun.aiScan.provider}` : null,
      advisorAutoLastRun.aiScan?.model ? `model: ${advisorAutoLastRun.aiScan.model}` : null,
    ].filter(Boolean).join("\n")
    : "Auto AI monitor has not completed yet.";
  const deepScanPhaseLabel = advisorDeepScan?.phase
    ? String(advisorDeepScan.phase).replace(/_/g, " ")
    : advisorDeepScan?.mode || "";

  useEffect(() => {
    if (!advisorEnabled || !advisorAutoLastRun?.finishedAt) return undefined;
    let cancelled = false;
    getAdvisorAnalyses()
      .then((payload) => {
        if (!cancelled && payload?.analyses) setAdvisorAnalyses(payload.analyses);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [advisorAutoLastRun?.finishedAt, advisorEnabled]);

  useEffect(() => {
    if (!advisorEnabled || tab !== "autoMonitor") return undefined;
    loadAutoMonitorHistory();
    const t = setInterval(loadAutoMonitorHistory, botStatus?.marketOpen ? 15000 : 45000);
    return () => clearInterval(t);
  }, [advisorEnabled, botStatus?.marketOpen, loadAutoMonitorHistory, tab]);

  useEffect(() => {
    if (!advisorEnabled || !advisorAutoLastRun?.finishedAt) return undefined;
    loadAutoMonitorHistory();
  }, [advisorAutoLastRun?.finishedAt, advisorEnabled, loadAutoMonitorHistory]);

  const aiActionColor = (action, urgency = 0) => {
    const a = String(action || "").toUpperCase();
    if (a === "EXIT" || a === "HIGH_RISK" || Number(urgency) >= 75) return "#ef4444";
    if (a === "TIGHTEN_STOP" || a === "PARTIAL_EXIT" || Number(urgency) >= 45) return "#f59e0b";
    if (a === "HOLD" || Number(urgency) < 35) return "#22c55e";
    return "#38bdf8";
  };

  const renderAiAnalysis = (symbol, pos = null) => {
    const analysis = advisorAnalyses[String(symbol || "").toUpperCase()];
    if (!analysis) {
      return (
        <div style={{ color: "#64748b", fontSize: 11, lineHeight: 1.45, maxWidth: 360 }}>
          No AI analysis yet. Use Analyze for a full candle-backed review.
        </div>
      );
    }
    const color = aiActionColor(analysis.action, analysis.exitUrgency);
    const riskBucket = String(analysis.riskBucket || "").toUpperCase();
    const analysisType = String(analysis.analysisType || "").replace(/_/g, " ").toUpperCase();
    const analyzedLabel = analysis.analyzedAt || analysis.savedAt ? formatIstDateTime(analysis.analyzedAt || analysis.savedAt) : null;
    const analysisMs = new Date(analysis.analyzedAt || analysis.savedAt || 0).getTime();
    const autoStartedMs = new Date(advisorAutoLastRun?.startedAt || 0).getTime();
    const autoFinishedMs = new Date(advisorAutoLastRun?.finishedAt || 0).getTime();
    const isAutoAnalysis = String(analysis.analysisType || "").toLowerCase() === "auto_monitor_scan";
    const showAutoRunningNote = advisorAutoMonitor?.running && !isAutoAnalysis && Number.isFinite(autoStartedMs) && autoStartedMs > analysisMs;
    const showAutoStaleNote = !advisorAutoMonitor?.running
      && advisorAutoLastRun?.status === "ok"
      && Number.isFinite(autoFinishedMs)
      && autoFinishedMs > analysisMs
      && !isAutoAnalysis;
    const lastAppliedType = String(pos?.aiStopTargetLastDecision?.analysisType || "").replace(/_/g, " ").toUpperCase();
    return (
      <div style={{ minWidth: portfolioExpanded && !isMobile ? 420 : 260, maxWidth: portfolioExpanded && !isMobile ? 540 : 380, fontSize: 11, lineHeight: 1.45 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{ color, fontWeight: 900 }}>{String(analysis.action || "WATCH").replace(/_/g, " ")}</span>
          {analysisType && <span style={{ color: "#38bdf8", fontSize: 9, fontWeight: 800 }}>{analysisType}</span>}
          {analyzedLabel && <span style={{ color: "#64748b", fontSize: 9 }}>{analyzedLabel}</span>}
          <span style={{ color: "#94a3b8" }}>Urgency {Number(analysis.exitUrgency || 0).toFixed(0)}/100</span>
          {riskBucket && <span style={{ color: "#94a3b8" }}>{riskBucket}</span>}
          {Number(analysis.confidence || 0) > 0 && <span style={{ color: "#64748b" }}>Conf {Number(analysis.confidence).toFixed(0)}%</span>}
        </div>
        {(showAutoRunningNote || showAutoStaleNote || lastAppliedType) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 5 }}>
            {showAutoRunningNote && (
              <span style={{ color: "#38bdf8", border: "1px solid #38bdf866", background: "#082f49", borderRadius: 4, padding: "2px 5px", fontSize: 9, fontWeight: 800 }}>
                AUTO MONITOR RUNNING - SHOWING PREVIOUS ANALYSIS
              </span>
            )}
            {showAutoStaleNote && (
              <span style={{ color: "#f59e0b", border: "1px solid #f59e0b66", background: "#1f1200", borderRadius: 4, padding: "2px 5px", fontSize: 9, fontWeight: 800 }}>
                NOT UPDATED BY LAST AUTO MONITOR
              </span>
            )}
            {lastAppliedType && (
              <span style={{ color: "#94a3b8", border: "1px solid #1e293b", background: "#07111f", borderRadius: 4, padding: "2px 5px", fontSize: 9, fontWeight: 800 }}>
                RISK LEVELS FROM {lastAppliedType}
              </span>
            )}
          </div>
        )}
        <div style={{ marginBottom: 5 }}>
          <CompactReason lines={portfolioExpanded && !isMobile ? null : 4} color="#cbd5e1">{analysis.summary || "AI returned no summary."}</CompactReason>
        </div>
        {(analysis.suggestedStopLoss || analysis.suggestedTargetPrice) && (
          <div style={{ color: "#94a3b8", fontSize: 10, marginBottom: 5 }}>
            {analysis.suggestedStopLoss ? `AI SL Rs ${Number(analysis.suggestedStopLoss).toFixed(2)}` : ""}
            {analysis.suggestedStopLoss && analysis.suggestedTargetPrice ? " | " : ""}
            {analysis.suggestedTargetPrice ? `AI Target Rs ${Number(analysis.suggestedTargetPrice).toFixed(2)}` : ""}
          </div>
        )}
        {Array.isArray(analysis.reasonCodes) && analysis.reasonCodes.length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 5 }}>
            {analysis.reasonCodes.slice(0, 5).map((code) => (
              <span key={code} style={{ background: "#07111f", border: "1px solid #1e293b", color: "#94a3b8", borderRadius: 4, padding: "2px 5px", fontSize: 9 }}>
                {code}
              </span>
            ))}
          </div>
        )}
        <div style={{ color: "#475569", fontSize: 10 }}>
          {analysis.analyzedAt ? `Analyzed ${formatIstDateTime(analysis.analyzedAt)}` : "Analysis time unavailable"}
          {analysis.contextStats?.fiveMinuteCandles != null ? ` · 5m ${analysis.contextStats.fiveMinuteCandles}` : ""}
        </div>
      </div>
    );
  };

  const renderMobilePositionCard = ([sym, pos]) => {
    const ltp = pos.currentPrice || pos.entry;
    const pnl = Number(pos.grossPnl ?? ((ltp - pos.entry) * pos.qty));
    const netPnl = Number(pos.netPnl ?? pnl);
    const pct = ((ltp - pos.entry) / pos.entry * 100);
    const invested = Number(pos.investedValue || ((pos.entry || 0) * (pos.qty || 0)));
    const isMonitor = monitorSet.has(sym);
    const isBlacklisted = blacklistSet.has(sym);
    const metricStyle = { display: "grid", gap: 3, minWidth: 0 };
    const labelStyle = { color: "#475569", fontSize: 9, fontWeight: 800, letterSpacing: "0.06em" };
    const valueStyle = { color: "#cbd5e1", fontSize: 12, fontFamily: "'DM Mono',monospace", overflowWrap: "anywhere" };

    return (
      <div key={sym} style={{ borderBottom: "1px solid #0f172a", padding: "14px 12px", display: "grid", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <SymbolCell
            symbol={sym}
            instrumentKey={pos.instrumentKey}
            onClick={() => onOpenSymbol?.({ symbol: sym, instrumentKey: pos.instrumentKey })}
            onOpenChart={(range) => openChartModal({ symbol: sym, instrumentKey: pos.instrumentKey, range })}
            onOpenScore={advisorEnabled ? () => openScoreChart({ symbol: sym, enteredAt: pos.enteredAt }) : null}
            onAnalyze={advisorEnabled ? () => analyzePosition(sym) : null}
            analyzing={analyzingSymbol === sym}
          />
          <div style={{ textAlign: "right", minWidth: 96 }}>
            <div style={{ color: netPnl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 800, fontSize: 13 }}>
              {netPnl >= 0 ? "+" : ""}Rs {netPnl.toFixed(2)}
            </div>
            <div style={{ color: pct >= 0 ? "#22c55e" : "#ef4444", fontSize: 11 }}>
              {pct >= 0 ? "+" : ""}{pct.toFixed(2)}%
            </div>
          </div>
        </div>

        {advisorEnabled && <div>{renderAiAnalysis(sym, pos)}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10 }}>
          <div style={metricStyle}><span style={labelStyle}>AMOUNT</span><span style={valueStyle}>{fmtAmountQtyCell(invested, pos.qty)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>LTP</span><span style={valueStyle}>Rs {ltp?.toFixed(2)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>ENTRY</span><span style={valueStyle}>Rs {pos.entry?.toFixed(2)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>HELD</span><span style={valueStyle}>{fmtHeldDays(pos.daysHeld, pos.holdingMs)}</span></div>
          <div style={metricStyle}>
            <span style={labelStyle}>STOP</span>
            <span style={{ ...valueStyle, color: "#ef4444" }}>
              {fmtPriceWithEntryPct(pos.stopLoss, pos.entry)}
              {aiManagedLabel("stop", pos)}
              {brokerManagedLabel("stop", pos)}
            </span>
          </div>
          <div style={metricStyle}>
            <span style={labelStyle}>TARGET</span>
            <span style={{ ...valueStyle, color: "#22c55e" }}>
              {fmtPriceWithEntryPct(pos.takeProfit, pos.entry)}
              {aiManagedLabel("target", pos)}
              {brokerManagedLabel("target", pos)}
            </span>
          </div>
          <div style={metricStyle}><span style={labelStyle}>TRAILING</span>{fmtTrailingStatus(pos)}</div>
          <div style={metricStyle}><span style={labelStyle}>MIN</span><span style={{ ...valueStyle, color: Number(pos.minGainPct || 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMinMaxCell(pos.minGainPct, pos.minPrice)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>MAX</span><span style={{ ...valueStyle, color: Number(pos.maxGainPct || 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMinMaxCell(pos.maxGainPct, pos.maxPrice)}</span></div>
        </div>

        <div style={{ color: "#64748b", fontSize: 11, lineHeight: 1.45 }}>
          {(pos.screenerLabels || []).length > 0 && (
            <div style={{ color: "#38bdf8", marginBottom: 3 }}>{pos.screenerLabels.join(", ")}</div>
          )}
          <CompactReason lines={4} color="#64748b">
            {pos.entryReason || (pos.entryScore != null ? `Score ${pos.entryScore}` : "-")}
          </CompactReason>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" disabled={exiting === sym || exitingAll} onClick={() => handleManualExit(sym)} style={{ background: "#2d0a0a", border: "1px solid #ef444466", color: "#f87171", borderRadius: 6, padding: "7px 9px", fontSize: 10, cursor: exiting === sym || exitingAll ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
            {exiting === sym ? "Exiting..." : "Exit"}
          </button>
          <button
            type="button"
            onClick={() => isMonitor ? moveToNormal(sym) : moveToMonitor(sym)}
            style={{ background: isMonitor ? "#07111f" : "#082f49", border: `1px solid ${isMonitor ? "#1e293b" : "#38bdf866"}`, color: isMonitor ? "#94a3b8" : "#38bdf8", borderRadius: 6, padding: "7px 9px", fontSize: 10, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            {isMonitor ? "Move Normal" : "Monitor"}
          </button>
          <button
            type="button"
            onClick={() => toggleBlacklist(sym)}
            style={{ background: isBlacklisted ? "#1f1200" : "#2d0a0a", border: `1px solid ${isBlacklisted ? "#f59e0b66" : "#ef444466"}`, color: isBlacklisted ? "#f59e0b" : "#f87171", borderRadius: 6, padding: "7px 9px", fontSize: 10, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            {isBlacklisted ? "Unblacklist" : "Blacklist"}
          </button>
        </div>
      </div>
    );
  };

  const renderMobileTradeCard = (t, index) => {
    const sym = String(t.symbol || "").toUpperCase();
    const isBlacklisted = blacklistSet.has(sym);
    const buyPrice = Number(t.buyPrice ?? t.entryPrice ?? 0);
    const exitPrice = Number(t.sellPrice ?? t.exitPrice ?? t.price ?? 0);
    const netPnl = Number(t.pnl || 0);
    const netPct = Number(tradeNetProfitPct(t) || 0);
    const metricStyle = { display: "grid", gap: 3, minWidth: 0 };
    const labelStyle = { color: "#475569", fontSize: 9, fontWeight: 800, letterSpacing: "0.06em" };
    const valueStyle = { color: "#cbd5e1", fontSize: 11, fontFamily: "'DM Mono',monospace", overflowWrap: "anywhere" };
    const openTrade = () => onOpenSymbol?.({
      symbol: t.symbol,
      instrumentKey: t.instrumentKey,
      trade: {
        entryTime: t.enteredAt || null,
        exitTime: t.timestamp || null,
        buyPrice: buyPrice || null,
        sellPrice: exitPrice || null,
        pnl: t.pnl ?? null,
        netProfitPct: tradeNetProfitPct(t),
      },
    });

    return (
      <div key={`${t.timestamp || index}-${sym}`} style={{ borderBottom: "1px solid #0f172a", padding: "14px 12px", display: "grid", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <SymbolCell
            symbol={t.symbol}
            instrumentKey={t.instrumentKey}
            onClick={openTrade}
            onOpenChart={(range) => openChartModal({
              symbol: t.symbol,
              instrumentKey: t.instrumentKey,
              range,
              trade: { entryTime: t.enteredAt || null, exitTime: t.timestamp || null, buyPrice, sellPrice: exitPrice },
            })}
            onOpenScore={advisorEnabled ? () => openScoreChart({ symbol: sym, enteredAt: t.enteredAt, exitedAt: t.timestamp || t.exitedAt }) : null}
          />
          <div style={{ textAlign: "right", minWidth: 104 }}>
            <div style={{ color: netPnl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 800, fontSize: 13 }}>
              {netPnl >= 0 ? "+" : ""}Rs {netPnl.toFixed(2)}
            </div>
            <div style={{ color: netPct >= 0 ? "#22c55e" : "#ef4444", fontSize: 11 }}>
              {netPct >= 0 ? "+" : ""}{netPct.toFixed(2)}%
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10 }}>
          <div style={metricStyle}><span style={labelStyle}>BUY</span><span style={valueStyle}>{fmtPrice(buyPrice)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>EXIT</span><span style={valueStyle}>{fmtPrice(exitPrice)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>MIN</span><span style={valueStyle}>{fmtMinMaxCell(t.minGainPct, t.minPrice)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>MAX</span><span style={valueStyle}>{fmtMinMaxCell(t.maxGainPct, t.maxPrice)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>HELD</span><span style={valueStyle}>{fmtHeldDays(t.daysHeld, t.holdingMs)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>COSTS</span><span style={{ ...valueStyle, color: "#f59e0b" }}>Rs {Number(t.costs || 0).toFixed(2)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>STOP AT EXIT</span><span style={valueStyle}>{fmtPrice(t.stopLossAtExit)}</span></div>
          <div style={metricStyle}><span style={labelStyle}>TARGET AT EXIT</span><span style={valueStyle}>{fmtPrice(t.takeProfitAtExit)}</span></div>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div>
            <div style={labelStyle}>ENTRY REASON</div>
            <CompactReason lines={4} color="#94a3b8">
              {t.entrySignalReason || t.entryReason || (t.entryScore != null ? `Score ${t.entryScore}` : "-")}
            </CompactReason>
          </div>
          <div>
            <div style={labelStyle}>EXIT REASON</div>
            <CompactReason lines={2} color={netPnl >= 0 ? "#22c55e" : "#f87171"}>
              {t.exitSignalReason || t.exitReason || t.status || "-"}
            </CompactReason>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" onClick={openTrade} style={{ background: "#0d1f35", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "7px 9px", fontSize: 10, cursor: "pointer" }}>View</button>
          <button type="button" onClick={() => toggleBlacklist(sym)} style={{ background: isBlacklisted ? "#1f1200" : "#2d0a0a", border: `1px solid ${isBlacklisted ? "#f59e0b66" : "#ef444466"}`, color: isBlacklisted ? "#f59e0b" : "#f87171", borderRadius: 6, padding: "7px 9px", fontSize: 10, cursor: "pointer" }}>
            {isBlacklisted ? "Unblacklist" : "Blacklist"}
          </button>
          <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 10 }}>{t.timestamp ? formatIstDateTime(t.timestamp) : "-"}</span>
        </div>
      </div>
    );
  };

  const modalRange = chartModal ? (MODAL_CHART_RANGES[chartModal.range] || MODAL_CHART_RANGES["1W"]) : MODAL_CHART_RANGES["1W"];
  const modalTradeMarkers = useMemo(() => {
    if (!chartModal?.trade) return [];
    const buyPrice = Number(chartModal.trade.buyPrice);
    const sellPrice = Number(chartModal.trade.sellPrice);
    return [
      Number.isFinite(buyPrice) ? { type: "entry", label: "BUY", time: chartModal.trade.entryTime, price: buyPrice, color: "#22c55e" } : null,
      Number.isFinite(sellPrice) ? { type: "exit", label: "SELL", time: chartModal.trade.exitTime, price: sellPrice, color: "#ef4444" } : null,
    ].filter(Boolean);
  }, [chartModal]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {scoreChartModal && (
        <div role="dialog" aria-modal="true" onClick={() => setScoreChartModal(null)} style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(2,6,23,0.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 10 : 24 }}>
          <div onClick={(event) => event.stopPropagation()} style={{ width: isMobile ? "96vw" : "78vw", height: isMobile ? "82vh" : "72vh", background: "#060d1a", border: "1px solid #334155", borderRadius: 8, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", borderBottom: "1px solid #0f172a", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ color: "#f1f5f9", fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 17 }}>{scoreChartModal.symbol} score history</div>
                <div style={{ color: "#64748b", fontSize: 10 }}>{scoreHistory.length} auto-monitor point{scoreHistory.length === 1 ? "" : "s"} for this trade</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" disabled={!scoreHistory.length} onClick={() => downloadCsv(`${scoreChartModal.symbol.toLowerCase()}-score-history`, scoreHistory)} style={{ background: "#0d1f35", border: "1px solid #38bdf8", color: scoreHistory.length ? "#38bdf8" : "#475569", borderRadius: 6, padding: "7px 10px", cursor: scoreHistory.length ? "pointer" : "not-allowed", fontSize: 10, fontWeight: 800 }}>Export scores</button>
                <button type="button" onClick={() => setScoreChartModal(null)} style={{ background: "#2d0a0a", border: "1px solid #ef444466", color: "#f87171", borderRadius: 6, padding: "7px 10px", cursor: "pointer", fontSize: 10 }}>Close</button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: isMobile ? 8 : 18 }}>
              {scoreHistoryLoading ? (
                <div style={{ color: "#64748b", padding: 24 }}>Loading score history...</div>
              ) : scoreHistoryError ? (
                <div style={{ color: "#f87171", padding: 24 }}>{scoreHistoryError}</div>
              ) : !scoreHistory.length ? (
                <div style={{ color: "#64748b", padding: 24 }}>No score points have been recorded for this trade yet. Points are added after each successful auto-monitor context build.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={scoreHistory.map((point) => ({ ...point, timeLabel: formatIstDateTime(point.recordedAt) }))} margin={{ top: 16, right: 24, left: 4, bottom: 24 }}>
                    <XAxis dataKey="timeLabel" tick={{ fill: "#64748b", fontSize: 9 }} minTickGap={42} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 9 }} width={52} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "#07111f", border: "1px solid #334155", color: "#cbd5e1", fontSize: 10 }} formatter={(value, name) => [Number(value).toFixed(3), name]} />
                    {scoreHistory[0]?.entryScore != null && scoreHistory[0]?.entryScore !== "" && Number.isFinite(Number(scoreHistory[0].entryScore)) && <ReferenceLine y={Number(scoreHistory[0].entryScore)} stroke="#f59e0b" strokeDasharray="5 4" label={{ value: "Entry", fill: "#f59e0b", fontSize: 9 }} />}
                    <Line type="monotone" dataKey="currentScore" name="Scanner score" stroke="#a78bfa" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="technicalScore" name="Technical component" stroke="#38bdf8" strokeWidth={1.4} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
      {chartModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(2,6,23,0.78)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? 10 : 20,
          }}
          onClick={closeChartModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isMobile ? "96vw" : "80vw",
              height: isMobile ? "86vh" : "80vh",
              background: "#060d1a",
              border: "1px solid #334155",
              borderRadius: 8,
              boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 14px", borderBottom: "1px solid #0f172a", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, color: "#f1f5f9" }}>{chartModal.symbol}</div>
                <div style={{ color: "#64748b", fontSize: 11 }}>{modalRange.label} · {modalRange.interval} candles</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {Object.keys(MODAL_CHART_RANGES).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => openChartModal({ ...chartModal, range: key })}
                    style={{
                      background: chartModal.range === key ? "#0d1f35" : "#07111f",
                      border: `1px solid ${chartModal.range === key ? "#38bdf8" : "#1e293b"}`,
                      color: chartModal.range === key ? "#38bdf8" : "#94a3b8",
                      borderRadius: 6,
                      padding: "6px 9px",
                      fontSize: 10,
                      cursor: "pointer",
                    }}
                  >
                    {MODAL_CHART_RANGES[key].label}
                  </button>
                ))}
                <button type="button" onClick={() => setModalChartMode((m) => (m === "line" ? "candles" : "line"))} style={{ background: "#07111f", border: "1px solid #334155", color: "#94a3b8", borderRadius: 6, padding: "6px 9px", fontSize: 10, cursor: "pointer" }}>
                  {modalChartMode === "line" ? "Line" : "Candles"}
                </button>
                <button type="button" onClick={closeChartModal} style={{ background: "#2d0a0a", border: "1px solid #ef444466", color: "#f87171", borderRadius: 6, padding: "6px 10px", fontSize: 10, cursor: "pointer" }}>
                  Close
                </button>
              </div>
            </div>
            {Number.isFinite(Number(modalChartView?.changePct)) && (
              <div style={{ padding: "8px 14px", borderBottom: "1px solid #0f172a", color: Number(modalChartView.changePct) >= 0 ? "#22c55e" : "#ef4444", fontSize: 12, fontWeight: 700 }}>
                Visible move: {Number(modalChartView.changePct) >= 0 ? "+" : ""}{Number(modalChartView.changePct).toFixed(2)}%
              </div>
            )}
            <div style={{ flex: 1, minHeight: 0, padding: isMobile ? 10 : 14, overflow: "hidden" }}>
              {modalLoading ? (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 12 }}>Loading chart...</div>
              ) : modalCandles.length > 5 ? (
                <CandleChart
                  candles={modalCandles}
                  width={modalSize.width}
                  height={modalSize.height}
                  mode={modalChartMode}
                  markers={modalTradeMarkers}
                  onViewChange={setModalChartView}
                />
              ) : (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: modalError ? "#f87171" : "#64748b", fontSize: 12 }}>
                  {modalError || "No chart data available."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Stats row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 11, color: "#64748b" }}>
          <span>
            {botStatus?.marketOpen
              ? "Live portfolio refresh is active while market is open."
              : "Market closed: showing cached/stored LTP. Use Refresh to reload local portfolio state."}
            {lastLoadedAt && <span> Last updated {formatIstTime(lastLoadedAt)} IST.</span>}
          </span>
          {advisorEnabled && (
            <span
              title={marketRegime?.reason || "Market regime has not been classified yet."}
              style={{ border: `1px solid ${marketRegimeColor}66`, color: marketRegimeColor, background: "#07111f", borderRadius: 6, padding: "4px 7px", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Market: {marketRegimeState}
              {marketRegimeIndexes.length > 0 && ` | ${marketRegimeIndexes.map((row) => `${row.label} ${Number.isFinite(Number(row.changePct)) ? `${Number(row.changePct) >= 0 ? "+" : ""}${Number(row.changePct).toFixed(2)}%` : "n/a"}`).join(" | ")}`}
            </span>
          )}
        </div>
        <button type="button" disabled={refreshing} onClick={loadNow}
          style={{ background: "#07111f", border: "1px solid #1e293b", color: refreshing ? "#64748b" : "#e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 11, cursor: refreshing ? "not-allowed" : "pointer" }}>
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(5,1fr)", gap: 10 }}>
        <StatCard label="REALIZED P&L" value={`${closedNetPnL >= 0 ? "+" : ""}Rs ${closedNetPnL.toFixed(0)}`} color={closedNetPnL >= 0 ? "#22c55e" : "#ef4444"} icon="Rs" />
        <StatCard label="CURRENT OPEN" value={`${openNetPnL >= 0 ? "+" : ""}Rs ${openNetPnL.toFixed(0)}`} color={openNetPnL >= 0 ? "#22c55e" : "#ef4444"} icon="Rs" />
        <StatCard label="WEEK PROFIT" value={`${weekProfit >= 0 ? "+" : ""}Rs ${weekProfit.toFixed(0)}`} color={weekProfit >= 0 ? "#22c55e" : "#ef4444"} icon="Rs" />
        <StatCard label="MONTH PROFIT" value={`${monthProfit >= 0 ? "+" : ""}Rs ${monthProfit.toFixed(0)}`} color={monthProfit >= 0 ? "#22c55e" : "#ef4444"} icon="Rs" />
        <StatCard label="AVAILABLE" value={`Rs ${Number(availableCapital || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} color="#38bdf8" icon="Rs" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(5,1fr)", gap: 10 }}>
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 8, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontSize: 10, color: "#475569", fontWeight: 700, letterSpacing: "0.08em" }}>PERIOD PROFIT</div>
            <select value={profitPeriod} onChange={(e) => handleProfitPeriodChange(e.target.value)} style={{ background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "5px 7px", fontSize: 10 }}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
              <option value="since">Since date</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: profitPeriod === "custom" && !isMobile ? "1fr 1fr" : "1fr", gap: 8, marginTop: 8 }}>
            <input
              type={profitPeriod === "month" ? "month" : profitPeriod === "year" ? "number" : "date"}
              min={profitPeriod === "year" ? "2020" : undefined}
              max={profitPeriod === "year" ? currentPeriodValueIST("year") : profitPeriod === "month" ? currentPeriodValueIST("month") : profitPeriod === "custom" ? profitCustomToDate : currentPeriodValueIST("day")}
              value={profitPeriodValue}
              onChange={(e) => setProfitPeriodValue(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "7px 8px", fontSize: 11 }}
            />
            {profitPeriod === "custom" && (
              <input
                type="date"
                min={profitPeriodValue}
                max={currentPeriodValueIST("day")}
                value={profitCustomToDate}
                onChange={(e) => setProfitCustomToDate(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "7px 8px", fontSize: 11 }}
              />
            )}
          </div>
          <div style={{ color: selectedPeriodProfit >= 0 ? "#22c55e" : "#ef4444", fontSize: 20, fontWeight: 800, marginTop: 8 }}>{selectedPeriodProfit >= 0 ? "+" : ""}Rs {selectedPeriodProfit.toFixed(2)}</div>
          <div style={{ color: "#64748b", fontSize: 10, marginTop: 5 }}>
            {selectedRange.label} · realized closed trades only
          </div>
        </div>
        <StatCard label="USED" value={`Rs ${Number(usedCapital || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} color="#f59e0b" icon="-" />
        <StatCard label="WIN RATE"  value={`${winRate}%`} color="#0ea5e9" icon="%" />
        <StatCard label="ORDERS" value={orderCount} color="#f59e0b" icon="*" />
      </div>
      {error && <div style={{ background: "#1f0a0a", border: "1px solid #7f1d1d44", color: "#f87171", borderRadius: 8, padding: 10, fontSize: 12 }}>{error}</div>}
      {notice && <div style={{ background: "#052e1a", border: "1px solid #22c55e44", color: "#22c55e", borderRadius: 8, padding: 10, fontSize: 12 }}>{notice}</div>}

      {/* Equity Curve */}
      {equityCurve.length > 1 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 10, color: "#475569", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 12 }}>EQUITY CURVE</div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={equityCurve}>
              <XAxis dataKey="i" hide />
              <YAxis domain={["auto","auto"]} tick={{ fontSize: 9, fill: "#475569" }} width={60} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: "#07111f", border: "1px solid #1e293b", borderRadius: 6, fontSize: 11 }}
                labelFormatter={l => `Trade #${l}`}
                formatter={v => [`₹${v.toFixed(0)}`, "Equity"]}
              />
              <ReferenceLine y={initialCapital} stroke="#334155" strokeDasharray="4 3" />
              <Line type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        background: "#060d1a",
        border: "1px solid #1e293b",
        borderRadius: portfolioExpanded ? 8 : 12,
        overflow: "hidden",
        position: portfolioExpanded ? "fixed" : "sticky",
        inset: portfolioExpanded ? 8 : "auto",
        top: portfolioExpanded ? 8 : (isMobile ? 54 : 62),
        height: portfolioExpanded ? "calc(100vh - 16px)" : `calc(100vh - ${isMobile ? 62 : 74}px)`,
        zIndex: portfolioExpanded ? 1200 : 20,
        display: "flex",
        flexDirection: "column",
        boxShadow: portfolioExpanded ? "0 24px 80px rgba(0,0,0,0.72)" : "none",
      }}>
        <div style={{ borderBottom: "1px solid #0f172a", display: "flex", alignItems: "stretch", minHeight: 38 }}>
          <div style={{ display: "flex", overflowX: "auto", minWidth: 0, flex: 1 }}>
            <TabBtn id="positions" label={`Open Positions (${openCount})`} />
            <TabBtn id="orders" label={`Orders (${orderCount})`} />
            <TabBtn id="trades" label={`Closed Trades (${trades.length})`} />
            {advisorEnabled && <TabBtn id="autoMonitor" label={`Auto Monitor (${autoMonitorHistory.total || autoMonitorScans.length})`} />}
            <TabBtn id="blacklist" label={`Blacklist (${blacklistSymbols.length})`} />
            {botStatus?.paperTrading && <TabBtn id="funds" label="Paper Funds" />}
          </div>
          <button
            type="button"
            onClick={() => setPortfolioExpanded((value) => !value)}
            title={portfolioExpanded ? "Exit full-screen portfolio" : "Open portfolio in full screen"}
            aria-label={portfolioExpanded ? "Exit full-screen portfolio" : "Open portfolio in full screen"}
            style={{
              flex: "0 0 auto",
              minWidth: 74,
              padding: "0 12px",
              background: portfolioExpanded ? "#10243a" : "#07111f",
              border: 0,
              borderLeft: "1px solid #1e293b",
              color: "#38bdf8",
              fontFamily: "'DM Mono',monospace",
              fontSize: 10,
              fontWeight: 800,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {portfolioExpanded ? "Restore" : "Pop out"}
          </button>
        </div>
        {activeExport && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid #0f172a", background: "#07111f", flexWrap: "wrap" }}>
            {tab === "positions" ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, minWidth: 0, maxWidth: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0, maxWidth: "100%" }}>
                  <button
                    type="button"
                    onClick={() => selectPositionView("all")}
                    style={{
                      background: positionView === "all" ? "#10243a" : "#07111f",
                      border: `1px solid ${positionView === "all" ? "#38bdf8" : "#1e293b"}`,
                      color: positionView === "all" ? "#38bdf8" : "#94a3b8",
                      borderRadius: 7,
                      padding: "7px 10px",
                      fontFamily: "'DM Mono',monospace",
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    All ({allOpenCount})
                  </button>
                  <button
                  type="button"
                  onClick={() => selectPositionView("normal")}
                  style={{
                    background: positionView === "normal" ? "#052e1a" : "#07111f",
                    border: `1px solid ${positionView === "normal" ? "#22c55e" : "#1e293b"}`,
                    color: positionView === "normal" ? "#22c55e" : "#94a3b8",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Normal ({normalOpenCount})
                </button>
                <button
                  type="button"
                  onClick={() => selectPositionView("monitor")}
                  style={{
                    background: positionView === "monitor" ? "#082f49" : "#07111f",
                    border: `1px solid ${positionView === "monitor" ? "#38bdf8" : "#1e293b"}`,
                    color: positionView === "monitor" ? "#38bdf8" : "#94a3b8",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Monitor ({monitorOpenCount})
                </button>
                {advisorEnabled && <button
                  type="button"
                  onClick={() => selectPositionView("risky")}
                  style={{
                    background: positionView === "risky" ? "#3a1212" : "#07111f",
                    border: `1px solid ${positionView === "risky" ? "#ef444466" : "#1e293b"}`,
                    color: positionView === "risky" ? "#f87171" : "#94a3b8",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Risky ({riskyOpenCount})
                </button>}
                {advisorEnabled && <button
                  type="button"
                  onClick={() => selectPositionView("ai")}
                  style={{
                    background: positionView === "ai" ? "#10243a" : "#07111f",
                    border: `1px solid ${positionView === "ai" ? "#38bdf8" : "#1e293b"}`,
                    color: positionView === "ai" ? "#38bdf8" : "#94a3b8",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  AI ({aiOpenCount})
                </button>}
                </div>
                {advisorEnabled && positionView === "ai" && (
                  <div style={{ display: "flex", gap: 6, overflowX: "auto", width: "100%", maxWidth: isMobile ? "100%" : 760, paddingBottom: 2 }}>
                    {aiActionFilterDefs.map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setAiActionFilter(id)}
                        style={{
                          background: aiActionFilter === id ? "#10243a" : "#07111f",
                          border: `1px solid ${aiActionFilter === id ? "#38bdf8" : "#1e293b"}`,
                          color: aiActionFilter === id ? "#38bdf8" : "#94a3b8",
                          borderRadius: 7,
                          padding: "7px 10px",
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label} ({aiActionCounts[id] || 0})
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 6, overflowX: "auto", width: "100%", maxWidth: isMobile ? "100%" : 880, paddingBottom: 2 }}>
                  {positionDataFilterDefs.map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setPositionDataFilter(id)}
                      style={{
                        background: positionDataFilter === id ? "#112012" : "#07111f",
                        border: `1px solid ${positionDataFilter === id ? "#22c55e66" : "#1e293b"}`,
                        color: positionDataFilter === id ? "#4ade80" : "#94a3b8",
                        borderRadius: 7,
                        padding: "7px 10px",
                        fontFamily: "'DM Mono',monospace",
                        fontSize: 10,
                        fontWeight: 700,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label} ({positionDataFilterCounts[id] || 0})
                    </button>
                  ))}
                </div>
              </div>
            ) : <span />}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {tab === "positions" && advisorEnabled && (
              <span
                title={advisorModelLabel || undefined}
                style={{
                  color: "#94a3b8",
                  border: "1px solid #1e293b",
                  borderRadius: 7,
                  padding: "7px 10px",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                AI: {advisorProviderLabel}
              </span>
            )}
            {tab === "positions" && advisorEnabled && advisorAutoMonitor?.enabled && (
              <span
                title={advisorAutoTitle}
                style={{
                  color: advisorAutoStatusColor,
                  border: `1px solid ${advisorAutoStatusColor}66`,
                  borderRadius: 7,
                  padding: "7px 10px",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {advisorAutoStatusText}
              </span>
            )}
            {tab === "positions" && advisorEnabled && advisorDeepScan?.running && deepScanPhaseLabel && (
              <span
                style={{
                  color: "#38bdf8",
                  border: "1px solid #1e3a5f",
                  borderRadius: 7,
                  padding: "7px 10px",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {deepScanPhaseLabel}
                {advisorDeepScan.contextCompleted != null ? ` ${advisorDeepScan.contextCompleted}/${advisorDeepScan.total || 0}` : ""}
              </span>
            )}
            {tab === "positions" && advisorEnabled && (
              <button
                type="button"
                disabled={startingDeepScan || advisorDeepScan?.running || openRows.length === 0 || !botStatus?.advisor?.apiKeyConfigured}
                onClick={() => startDeepScanAll("sequential")}
                style={{
                  background: "#052e1a",
                  border: "1px solid #22c55e66",
                  color: startingDeepScan || advisorDeepScan?.running || openRows.length === 0 || !botStatus?.advisor?.apiKeyConfigured ? "#166534" : "#22c55e",
                  borderRadius: 7,
                  padding: "7px 10px",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: startingDeepScan || advisorDeepScan?.running || openRows.length === 0 || !botStatus?.advisor?.apiKeyConfigured ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {advisorDeepScan?.running && advisorDeepScan?.mode !== "bulk"
                  ? `AI Deep ${advisorDeepScan.completed || 0}/${advisorDeepScan.total || 0}`
                  : startingDeepScan
                    ? "Starting AI..."
                    : `AI Deep Scan (${openRows.length})`}
              </button>
            )}
            {tab === "positions" && advisorEnabled && advisorBulkEnabled && (
              <button
                type="button"
                disabled={startingDeepScan || advisorDeepScan?.running || openRows.length === 0 || !botStatus?.advisor?.apiKeyConfigured}
                onClick={() => startDeepScanAll("bulk")}
                style={{
                  background: "#10243a",
                  border: "1px solid #38bdf866",
                  color: startingDeepScan || advisorDeepScan?.running || openRows.length === 0 || !botStatus?.advisor?.apiKeyConfigured ? "#1e3a5f" : "#38bdf8",
                  borderRadius: 7,
                  padding: "7px 10px",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: startingDeepScan || advisorDeepScan?.running || openRows.length === 0 || !botStatus?.advisor?.apiKeyConfigured ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {advisorDeepScan?.running && advisorDeepScan?.mode === "bulk"
                  ? `AI Bulk ${advisorDeepScan.completed || 0}/${advisorDeepScan.total || 0}`
                  : startingDeepScan
                    ? "Starting AI..."
                    : `AI Bulk Scan (${openRows.length})`}
              </button>
            )}
            {tab === "positions" && (
              <button
                type="button"
                disabled={exitingAll || visibleOpenRows.length === 0}
                onClick={handleExitAll}
                style={{
                  background: "#2d0a0a",
                  border: "1px solid #ef444466",
                  color: exitingAll || visibleOpenRows.length === 0 ? "#7f1d1d" : "#f87171",
                  borderRadius: 7,
                  padding: "7px 10px",
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: exitingAll || visibleOpenRows.length === 0 ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {exitingAll
                  ? "Exiting all..."
                  : `Exit All ${exitAllLabel} (${visibleOpenRows.length})`}
              </button>
            )}
            {tab === "autoMonitor" && (
              <>
                <button
                  type="button"
                  disabled={autoMonitorLoading}
                  onClick={loadAutoMonitorHistory}
                  style={{
                    background: "#07111f",
                    border: "1px solid #1e293b",
                    color: autoMonitorLoading ? "#475569" : "#94a3b8",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: autoMonitorLoading ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {autoMonitorLoading ? "Refreshing..." : "Refresh"}
                </button>
                <button
                  type="button"
                  disabled={!autoMonitorScans.length}
                  onClick={clearAutoMonitorHistory}
                  style={{
                    background: "#2d0a0a",
                    border: "1px solid #ef444466",
                    color: autoMonitorScans.length ? "#f87171" : "#7f1d1d",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: autoMonitorScans.length ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap",
                  }}
                >
                  Clear History
                </button>
              </>
            )}
            {tab === "trades" && (
              <>
                <button
                  type="button"
                  disabled={!closedTradeExportRows.length}
                  onClick={() => downloadCsv("closed-trades", closedTradeExportRows)}
                  style={{
                    background: "#07111f",
                    border: "1px solid #64748b66",
                    color: closedTradeExportRows.length ? "#94a3b8" : "#475569",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: closedTradeExportRows.length ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap",
                  }}
                >
                  Base Trades CSV
                </button>
                <button
                  type="button"
                  disabled={!latestClosedTradeExportRows.length}
                  onClick={() => exportPortfolioAudit({ scope: "closed", latest20: true })}
                  style={{
                    background: "#07111f",
                    border: "1px solid #22c55e66",
                    color: latestClosedTradeExportRows.length ? "#4ade80" : "#475569",
                    borderRadius: 7,
                    padding: "7px 10px",
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: latestClosedTradeExportRows.length ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap",
                  }}
                >
                  Last 20 Audit JSON
                </button>
              </>
            )}
            <button
              type="button"
              disabled={!activeExport.rows.length}
              onClick={() => tab === "autoMonitor"
                ? exportAutoMonitorHistory()
                : tab === "positions"
                  ? exportPortfolioAudit({ scope: "open" })
                  : tab === "trades"
                    ? exportPortfolioAudit({ scope: "closed" })
                    : downloadCsv(activeExport.file, activeExport.rows)}
              style={{
                background: "#0d1f35",
                border: "1px solid #38bdf8",
                color: activeExport.rows.length ? "#38bdf8" : "#475569",
                borderRadius: 7,
                padding: "7px 10px",
                fontFamily: "'DM Mono',monospace",
                fontSize: 10,
                fontWeight: 700,
                cursor: activeExport.rows.length ? "pointer" : "not-allowed",
                whiteSpace: "nowrap",
              }}
            >
              {tab === "trades" ? "Download Closed Audit JSON" : activeExport.label}
            </button>
            </div>
          </div>
        )}

        <div style={{ padding: 0, flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* Open Positions */}
          {tab === "positions" && (
            Object.keys(positions).length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#334155", fontSize: 12 }}>
                No open positions. The bot will enter when signals are strong enough.
              </div>
            ) : visibleOpenRows.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#334155", fontSize: 12 }}>
                No {positionViewLabel} positions{positionView === "ai" && aiActionFilter !== "ALL" ? ` matching ${aiActionFilter.replace(/_/g, " ")}` : ""}. Use the Action column to move open positions between Normal and Monitor.
              </div>
            ) : isMobile ? (
              <div style={{ height: "100%", minHeight: 0, overflowY: "auto" }}>
                {visibleOpenRows.map(renderMobilePositionCard)}
              </div>
            ) : (
              <div style={{ height: "100%", minHeight: 0, overflow: "auto" }}>
              <table style={{ width: portfolioExpanded ? 2720 : 2440, borderCollapse: "collapse", fontSize: portfolioExpanded ? 10 : 11, minWidth: portfolioExpanded ? 2720 : 2440, tableLayout: "fixed" }}>
                <thead>
                  <tr style={{ background: "#07111f", position: "sticky", top: 0 }}>
                    {["Symbol", ...(advisorEnabled ? ["AI Analysis"] : []), "Amount (Qty)","Min Price","Entry","Max Price","LTP","P&L%","Net P&L","Held","Action","Trailing SL","Signal","Bought","Gross P&L","Costs","Stop Loss","Take Profit"].map(h => (
                      <th
                        key={h}
                        style={{
                          padding: portfolioExpanded ? "8px 5px" : "9px 7px",
                          textAlign: "left",
                          fontSize: 10,
                          color: "#475569",
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          width: 90,
                          ...(h === "Symbol" ? { width: 140 } : {}),
                          ...(h === "AI Analysis" ? { width: portfolioExpanded ? 500 : 370 } : {}),
                          ...(h === "Amount (Qty)" ? { width: 115 } : {}),
                          ...(h === "Action" ? { width: 105 } : {}),
                          ...(h === "Signal" ? { width: portfolioExpanded ? 500 : 370 } : {}),
                          ...(h === "Bought" ? { width: 145 } : {}),
                          ...(h === "Trailing SL" ? { width: 110 } : {}),
                          ...(["Stop Loss", "Take Profit"].includes(h) ? { width: 165 } : {}),
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleOpenRows.map(([sym, pos]) => {
                    const ltp  = pos.currentPrice || pos.entry;
                    const pnl  = Number(pos.grossPnl ?? ((ltp - pos.entry) * pos.qty));
                    const netPnl = Number(pos.netPnl ?? pnl);
                    const pct  = ((ltp - pos.entry) / pos.entry * 100);
                    const invested = Number(pos.investedValue || ((pos.entry || 0) * (pos.qty || 0)));
                    const isMonitor = monitorSet.has(sym);
                    const isBlacklisted = blacklistSet.has(sym);
                    return (
                      <tr key={sym} style={{ borderBottom: "1px solid #07111f" }}>
                        <td style={{ padding: "12px 14px" }}>
                          <SymbolCell
                            symbol={sym}
                            instrumentKey={pos.instrumentKey}
                            onClick={() => onOpenSymbol?.({ symbol: sym, instrumentKey: pos.instrumentKey })}
                            onOpenChart={(range) => openChartModal({ symbol: sym, instrumentKey: pos.instrumentKey, range })}
                            onOpenScore={advisorEnabled ? () => openScoreChart({ symbol: sym, enteredAt: pos.enteredAt }) : null}
                            onAnalyze={advisorEnabled ? () => analyzePosition(sym) : null}
                            analyzing={analyzingSymbol === sym}
                          />
                        </td>
                        {advisorEnabled && <td style={{ padding: "12px 14px", verticalAlign: "top", width: portfolioExpanded ? 500 : 370 }}>{renderAiAnalysis(sym, pos)}</td>}
                        <td style={{ padding: "12px 14px" }}>{fmtAmountQtyCell(invested, pos.qty)}</td>
                        <td style={{ padding: "12px 14px", color: Number(pos.minGainPct || 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMinMaxCell(pos.minGainPct, pos.minPrice)}</td>
                        <td style={{ padding: "12px 14px" }}>Rs {pos.entry?.toFixed(2)}</td>
                        <td style={{ padding: "12px 14px", color: Number(pos.maxGainPct || 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMinMaxCell(pos.maxGainPct, pos.maxPrice)}</td>
                        <td style={{ padding: "12px 14px" }}>
                          <div>Rs {ltp?.toFixed(2)}</div>
                          {pos.lastPriceSource && (
                            <div style={{ marginTop: 3, fontSize: 10, color: "#64748b", whiteSpace: "nowrap" }}>
                              {pos.lastPriceSource === "upstox_quote" ? "Upstox quote" : pos.lastPriceSource}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "12px 14px", color: pct >= 0 ? "#22c55e" : "#ef4444" }}>
                          {pct >= 0 ? "+" : ""}{pct.toFixed(2)}%
                        </td>
                        <td style={{ padding: "12px 14px", color: netPnl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                          {netPnl >= 0 ? "+" : ""}Rs {netPnl.toFixed(2)}
                        </td>
                        <td style={{ padding: "12px 14px", color: "#64748b" }}>{fmtHeldDays(pos.daysHeld, pos.holdingMs)}</td>
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                            <button type="button" disabled={exiting === sym || exitingAll} onClick={() => handleManualExit(sym)} style={{ background: "#2d0a0a", border: "1px solid #ef444466", color: "#f87171", borderRadius: 6, padding: "5px 8px", fontSize: 10, cursor: exiting === sym || exitingAll ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                              {exiting === sym ? "Exiting..." : "Exit"}
                            </button>
                            <button
                              type="button"
                              onClick={() => isMonitor ? moveToNormal(sym) : moveToMonitor(sym)}
                              style={{
                                background: isMonitor ? "#07111f" : "#082f49",
                                border: `1px solid ${isMonitor ? "#1e293b" : "#38bdf866"}`,
                                color: isMonitor ? "#94a3b8" : "#38bdf8",
                                borderRadius: 6,
                                padding: "5px 8px",
                                fontSize: 10,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isMonitor ? "Move Normal" : "Monitor"}
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleBlacklist(sym)}
                              style={{
                                background: isBlacklisted ? "#1f1200" : "#2d0a0a",
                                border: `1px solid ${isBlacklisted ? "#f59e0b66" : "#ef444466"}`,
                                color: isBlacklisted ? "#f59e0b" : "#f87171",
                                borderRadius: 6,
                                padding: "5px 8px",
                                fontSize: 10,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isBlacklisted ? "Unblacklist" : "Blacklist"}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: "12px 14px" }}>{fmtTrailingStatus(pos)}</td>
                        <td style={{ padding: "12px 14px", color: "#64748b", width: portfolioExpanded ? 500 : 370, fontSize: 11, lineHeight: 1.45, verticalAlign: "top" }}>
                          {(pos.screenerLabels || []).length > 0 && (
                            <div style={{ color: "#38bdf8", marginBottom: 3 }}>{pos.screenerLabels.join(", ")}</div>
                          )}
                          <CompactReason lines={portfolioExpanded ? null : 4} color="#64748b">
                            {pos.entryReason || (pos.entryScore != null ? `Score ${pos.entryScore}` : "-")}
                          </CompactReason>
                        </td>
                        <td style={{ padding: "12px 14px", color: "#94a3b8", fontSize: 10, whiteSpace: "nowrap" }}>{pos.enteredAt ? formatIstDateTime(pos.enteredAt) : "—"}</td>
                        <td style={{ padding: "12px 14px", color: pnl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                          {pnl >= 0 ? "+" : ""}Rs {pnl.toFixed(2)}
                        </td>
                        <td style={{ padding: "12px 14px", color: "#f59e0b" }}>Rs {Number(pos.estimatedCosts || 0).toFixed(2)}</td>
                        <td style={{ padding: "12px 14px", color: "#ef4444", whiteSpace: "nowrap", width: 165 }}>
                          {fmtPriceWithEntryPct(pos.stopLoss, pos.entry)}
                          {aiManagedLabel("stop", pos)}
                          {brokerManagedLabel("stop", pos)}
                        </td>
                        <td style={{ padding: "12px 14px", color: "#22c55e", whiteSpace: "nowrap", width: 165 }}>
                          {fmtPriceWithEntryPct(pos.takeProfit, pos.entry)}
                          {aiManagedLabel("target", pos)}
                          {brokerManagedLabel("target", pos)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )
          )}

          {/* Auto Monitor History */}
          {tab === "autoMonitor" && (
            autoMonitorRows.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#334155", fontSize: 12 }}>
                No auto-monitor scan history yet. The next auto-monitor run will be stored here with per-symbol status and failure reasons.
              </div>
            ) : (
              <div style={{ height: "100%", minHeight: 0, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1780 }}>
                  <thead>
                    <tr style={{ background: "#07111f", position: "sticky", top: 0, zIndex: 1 }}>
                      {["Scan Time","Run","Symbol","Updated","Decision","Action","Score","Urgency","Conf","Move","SL / Target","Provider","Reason / Error","Delete"].map((h) => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, color: "#475569", fontWeight: 700, letterSpacing: "0.06em" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {autoMonitorRows.map((row, index) => {
                      const scan = row.scan || {};
                      const fresh = !!row.updatedByScan;
                      const statusColor = fresh ? "#22c55e" : row.status === "CONTEXT_ERROR" || row.status === "ERROR" ? "#ef4444" : "#f59e0b";
                      const action = String(row.action || "-").replace(/_/g, " ");
                      const move = String(row.monitorMove || "-").replace(/_/g, " ");
                      const sl = Number(row.suggestedStopLoss);
                      const tp = Number(row.suggestedTargetPrice);
                      const slTp = [
                        Number.isFinite(sl) && sl > 0 ? `SL Rs ${sl.toFixed(2)}` : null,
                        Number.isFinite(tp) && tp > 0 ? `TP Rs ${tp.toFixed(2)}` : null,
                      ].filter(Boolean).join(" | ") || "-";
                      const riskBits = [
                        row.stopLossChanged ? `SL ${fmtPrice(row.oldStopLoss)} -> ${fmtPrice(row.newStopLoss)}` : null,
                        row.takeProfitChanged ? `TP ${fmtPrice(row.oldTakeProfit)} -> ${fmtPrice(row.newTakeProfit)}` : null,
                        row.brokerSyncStatus ? `Upstox ${row.brokerSyncStatus}` : null,
                        row.brokerSyncError ? `Upstox error: ${row.brokerSyncError}` : null,
                      ].filter(Boolean).join(" | ");
                      return (
                        <tr key={`${scan.id || "scan"}-${row.symbol || "summary"}-${index}`} style={{ borderBottom: "1px solid #07111f", background: index % 2 === 0 ? "#060d1a" : "#07111f" }}>
                          <td style={{ padding: "10px 12px", color: "#64748b", whiteSpace: "nowrap" }}>
                            <div>{scan.finishedAt ? formatIstDateTime(scan.finishedAt) : scan.startedAt ? formatIstDateTime(scan.startedAt) : "-"}</div>
                            <div style={{ color: "#475569", fontSize: 10 }}>{scan.id ? scan.id.slice(0, 8) : ""}</div>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#94a3b8", minWidth: 150 }}>
                            <div style={{ color: scan.status === "ok" ? "#22c55e" : scan.status === "error" ? "#ef4444" : "#f59e0b", fontWeight: 800 }}>
                              {String(scan.status || "unknown").toUpperCase()}
                            </div>
                            <div style={{ color: "#64748b", fontSize: 10 }}>
                              {scan.reason || "-"} · {Number(scan.analyzed || 0)}/{Number(scan.openPositions || 0)}
                              {Number(scan.jsonRepaired || 0) > 0 ? ` · repaired JSON ${Number(scan.jsonRepaired)}` : ""}
                            </div>
                            {scan.aiError && <div style={{ color: "#ef4444", fontSize: 10 }}>{scan.aiError}</div>}
                          </td>
                          <td style={{ padding: "10px 12px", color: row.symbol ? "#38bdf8" : "#64748b", fontWeight: 800, whiteSpace: "nowrap" }}>
                            {row.symbol || "RUN SUMMARY"}
                          </td>
                          <td style={{ padding: "10px 12px", color: statusColor, fontWeight: 800, whiteSpace: "nowrap" }}>
                            {fresh ? "UPDATED" : String(row.status || "NOT UPDATED").replace(/_/g, " ")}
                          </td>
                          <td style={{ padding: "10px 12px", color: row.decision === "RISKY" ? "#ef4444" : row.decision === "MONITOR" ? "#f59e0b" : row.decision === "NORMAL" ? "#22c55e" : "#94a3b8", fontWeight: 800 }}>
                            {String(row.decision || "-").replace(/_/g, " ")}
                          </td>
                          <td style={{ padding: "10px 12px", color: "#cbd5e1" }}>{action}</td>
                          <td style={{ padding: "10px 12px", color: Number(row.scoreChangeFromEntry || 0) < 0 ? "#f87171" : "#a78bfa", whiteSpace: "nowrap" }}>
                            {row.currentScore == null ? "-" : Number(row.currentScore).toFixed(3)}
                            {row.scoreChangeFromEntry != null && <div style={{ color: Number(row.scoreChangeFromEntry) < 0 ? "#f87171" : "#22c55e", fontSize: 10 }}>{Number(row.scoreChangeFromEntry) >= 0 ? "+" : ""}{Number(row.scoreChangeFromEntry).toFixed(3)} from entry</div>}
                            {row.currentScore == null && row.scoreError && <div title={row.scoreError} style={{ color: "#f59e0b", fontSize: 10, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>unavailable</div>}
                          </td>
                          <td style={{ padding: "10px 12px", color: Number(row.urgency || 0) >= 45 ? "#f59e0b" : "#94a3b8", whiteSpace: "nowrap" }}>
                            {row.urgency == null ? "-" : `${Number(row.urgency).toFixed(0)}/100`}
                            {row.riskBucket ? <div style={{ color: "#64748b", fontSize: 10 }}>{row.riskBucket}</div> : null}
                          </td>
                          <td style={{ padding: "10px 12px", color: "#94a3b8" }}>{row.confidence == null ? "-" : `${Number(row.confidence).toFixed(0)}%`}</td>
                          <td style={{ padding: "10px 12px", color: move.includes("RISKY") ? "#ef4444" : move.includes("MONITOR") ? "#38bdf8" : "#94a3b8", whiteSpace: "nowrap" }}>{move}</td>
                          <td style={{ padding: "10px 12px", color: row.riskUpdated ? "#22c55e" : "#94a3b8", minWidth: 180 }}>
                            <div>{slTp}</div>
                            {riskBits && <div style={{ color: "#38bdf8", fontSize: 10 }}>Applied: {riskBits}</div>}
                          </td>
                          <td style={{ padding: "10px 12px", color: "#64748b", minWidth: 150 }}>
                            <div>{row.provider || scan.provider || "-"}</div>
                            <div style={{ fontSize: 10 }}>{row.model || scan.model || "-"}</div>
                          </td>
                          <td style={{ padding: "10px 12px", color: row.error ? "#f87171" : "#cbd5e1", minWidth: 420, maxWidth: 560, lineHeight: 1.45 }}>
                            {row.error || row.summary || "-"}
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            {index === autoMonitorRows.findIndex((candidate) => candidate.scan?.id === scan.id) && (
                              <button
                                type="button"
                                onClick={() => deleteAutoMonitorRun(scan.id)}
                                style={{
                                  background: "#2d0a0a",
                                  border: "1px solid #ef444466",
                                  color: "#f87171",
                                  borderRadius: 6,
                                  padding: "5px 8px",
                                  fontSize: 10,
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Delete run
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Orders */}
          {tab === "orders" && (
            orders.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#334155", fontSize: 12 }}>No orders yet. Automatic bot entries and scanner Paper Buy orders will appear here.</div>
            ) : (
              <div style={{ height: "100%", minHeight: 0, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 760 }}>
                  <thead>
                    <tr style={{ background: "#07111f", position: "sticky", top: 0 }}>
                      {["Date","Time","Order ID","Symbol","Side","Qty","Price","Value","Status"].map(h => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, color: "#475569", fontWeight: 600, letterSpacing: "0.08em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...orders].reverse().map((o, i) => (
                      <tr key={o.orderId || i} style={{ borderBottom: "1px solid #07111f", background: i % 2 === 0 ? "#060d1a" : "#07111f" }}>
                        <td style={{ padding: "10px 14px", color: "#64748b", fontSize: 10, whiteSpace: "nowrap" }}>{o.timestamp ? formatIstDateTime(o.timestamp) : "-"}</td>
                        <td style={{ padding: "10px 14px", color: "#475569", fontSize: 10 }}>{o.timestamp ? formatIstTime(o.timestamp) : "-"}</td>
                        <td style={{ padding: "10px 14px", color: "#64748b", fontSize: 10, fontFamily: "'DM Mono',monospace" }}>{o.orderId || "-"}</td>
                        <td style={{ padding: "10px 14px", fontWeight: 700, color: "#38bdf8", fontFamily: "'Syne',sans-serif", cursor: "pointer", textDecoration: "underline" }} onClick={() => onOpenSymbol?.({ symbol: o.symbol, instrumentKey: o.instrumentKey })}>{o.symbol}</td>
                        <td style={{ padding: "10px 14px", color: o.side === "BUY" ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{o.side}</td>
                        <td style={{ padding: "10px 14px" }}>{o.qty}</td>
                        <td style={{ padding: "10px 14px" }}>Rs {Number(o.price || 0).toFixed(2)}</td>
                        <td style={{ padding: "10px 14px" }}>Rs {Number(o.value || ((o.price || 0) * (o.qty || 0))).toFixed(0)}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 9, background: "#0f172a", border: "1px solid #1e293b", color: o.paper ? "#f59e0b" : "#38bdf8", borderRadius: 3, padding: "2px 6px" }}>
                            {o.paper ? "PAPER" : "LIVE"} {o.status || "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Trade History */}
          {tab === "trades" && (
            trades.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#334155", fontSize: 12 }}>No trades yet.</div>
            ) : isMobile ? (
              <div style={{ height: "100%", minHeight: 0, overflowY: "auto" }}>
                {[...trades].reverse().map(renderMobileTradeCard)}
              </div>
            ) : (
              <div style={{ height: "100%", minHeight: 0, overflow: "auto" }}>
                <table style={{ width: portfolioExpanded ? 3420 : 3100, borderCollapse: "collapse", fontSize: portfolioExpanded ? 9 : 10, minWidth: portfolioExpanded ? 3420 : 3100, tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ background: "#07111f", position: "sticky", top: 0 }}>
                      {["Symbol","Amount (Qty)","Min Price","Buy","Max Price","Exit","Trade %","Net P&L","Held","Action","Entry Reason","Exit Reason","Exit Date","Exit Time","Bought","Side","Profit %","Stock @ Buy","Stock @ Sell","SL","TP","Held Time","Value","Gross","Costs"].map(h => (
                        <th key={h} style={{
                          padding: portfolioExpanded ? "8px 4px" : "9px 6px",
                          textAlign: "left",
                          fontSize: 10,
                          color: "#475569",
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          width: 90,
                          ...(h === "Symbol" ? { width: 130 } : {}),
                          ...(h === "Amount (Qty)" ? { width: 110 } : {}),
                          ...(h === "Action" ? { width: 90 } : {}),
                          ...(h === "Entry Reason" ? { width: portfolioExpanded ? 460 : 350 } : {}),
                          ...(h === "Exit Reason" ? { width: portfolioExpanded ? 380 : 280 } : {}),
                          ...(["Exit Date", "Bought"].includes(h) ? { width: 135 } : {}),
                          ...(h === "Exit Time" ? { width: 100 } : {}),
                          ...(["SL", "TP"].includes(h) ? { width: 120 } : {}),
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...trades].reverse().map((t, i) => {
                      const sym = String(t.symbol || "").toUpperCase();
                      const isBlacklisted = blacklistSet.has(sym);
                      return (
                      <tr key={i} style={{ borderBottom: "1px solid #07111f", background: i % 2 === 0 ? "#060d1a" : "#07111f" }}>
                        <td style={{ padding: "10px 14px" }}>
                          <SymbolCell
                            symbol={t.symbol}
                            instrumentKey={t.instrumentKey}
                            onOpenChart={(range) => openChartModal({
                              symbol: t.symbol,
                              instrumentKey: t.instrumentKey,
                              range,
                              trade: {
                                entryTime: t.enteredAt || null,
                                exitTime: t.timestamp || null,
                                buyPrice: t.buyPrice ?? t.entryPrice ?? null,
                                sellPrice: t.sellPrice ?? t.exitPrice ?? t.price ?? null,
                              },
                            })}
                            onOpenScore={advisorEnabled ? () => openScoreChart({ symbol: sym, enteredAt: t.enteredAt, exitedAt: t.timestamp || t.exitedAt }) : null}
                            onClick={() => onOpenSymbol?.({
                              symbol: t.symbol,
                              instrumentKey: t.instrumentKey,
                              trade: {
                                entryTime: t.enteredAt || null,
                                exitTime: t.timestamp || null,
                                buyPrice: t.buyPrice ?? t.entryPrice ?? null,
                                sellPrice: t.sellPrice ?? t.exitPrice ?? t.price ?? null,
                                pnl: t.pnl ?? null,
                                netProfitPct: tradeNetProfitPct(t),
                              },
                            })}
                          />
                        </td>
                        <td style={{ padding: "10px 14px" }}>{fmtAmountQtyCell(t.buyValue ?? ((t.buyPrice ?? t.entryPrice ?? 0) * (t.qty || 0)), t.qty)}</td>
                        <td style={{ padding: "10px 14px", color: Number(t.minGainPct || 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMinMaxCell(t.minGainPct, t.minPrice)}</td>
                        <td style={{ padding: "10px 14px" }}>{fmtPrice(t.buyPrice ?? t.entryPrice)}</td>
                        <td style={{ padding: "10px 14px", color: Number(t.maxGainPct || 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMinMaxCell(t.maxGainPct, t.maxPrice)}</td>
                        <td style={{ padding: "10px 14px" }}>{fmtPrice(t.sellPrice ?? t.exitPrice ?? t.price)}</td>
                        <td style={{ padding: "10px 14px", color: Number(t.tradeReturnPct || 0) >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{fmtPct(t.tradeReturnPct)}</td>
                        <td style={{ padding: "10px 14px", color: (t.pnl || 0) >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                          {t.pnl != null ? `${t.pnl >= 0 ? "+" : ""}Rs ${t.pnl.toFixed(2)}` : "-"}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#64748b" }}>{fmtHeldDays(t.daysHeld, t.holdingMs)}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                            <button type="button" onClick={() => onOpenSymbol?.({
                              symbol: t.symbol,
                              instrumentKey: t.instrumentKey,
                              trade: {
                                entryTime: t.enteredAt || null,
                                exitTime: t.timestamp || null,
                                buyPrice: t.buyPrice ?? t.entryPrice ?? null,
                                sellPrice: t.sellPrice ?? t.exitPrice ?? t.price ?? null,
                                pnl: t.pnl ?? null,
                                netProfitPct: tradeNetProfitPct(t),
                              },
                            })} style={{ background: "#0d1f35", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "5px 8px", fontSize: 10, cursor: "pointer", whiteSpace: "nowrap" }}>
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleBlacklist(sym)}
                              style={{
                                background: isBlacklisted ? "#1f1200" : "#2d0a0a",
                                border: `1px solid ${isBlacklisted ? "#f59e0b66" : "#ef444466"}`,
                                color: isBlacklisted ? "#f59e0b" : "#f87171",
                                borderRadius: 6,
                                padding: "5px 8px",
                                fontSize: 10,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isBlacklisted ? "Unblacklist" : "Blacklist"}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#64748b", width: portfolioExpanded ? 460 : 350, fontSize: 10, lineHeight: 1.45, verticalAlign: "top" }}>
                          {(t.screenerLabels || []).length > 0 && (
                            <div style={{ color: "#38bdf8", marginBottom: 3 }}>{t.screenerLabels.join(", ")}</div>
                          )}
                          <CompactReason lines={portfolioExpanded ? null : 4} color="#64748b">
                            {t.entrySignalReason || t.entryReason || (t.entryScore != null ? `Score ${t.entryScore}` : "-")}
                          </CompactReason>
                        </td>
                        <td style={{ padding: "10px 14px", width: portfolioExpanded ? 380 : 280, verticalAlign: "top", fontSize: 10, lineHeight: 1.45 }}>
                          <CompactReason lines={portfolioExpanded ? null : 3} color={(t.grossPnl ?? t.pnl ?? 0) >= 0 ? "#22c55e" : "#f87171"}>
                            {t.exitSignalReason || t.exitReason || t.status || "-"}
                          </CompactReason>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#64748b", fontSize: 10, whiteSpace: "nowrap" }}>{t.timestamp ? formatIstDateTime(t.timestamp) : "—"}</td>
                        <td style={{ padding: "10px 14px", color: "#475569", fontSize: 10 }}>{t.timestamp ? formatIstTime(t.timestamp) : "—"}</td>
                        <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 10, whiteSpace: "nowrap" }}>{t.enteredAt ? formatIstDateTime(t.enteredAt) : "—"}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ color: t.side === "BUY" ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{t.side}</span>
                        </td>
                        <td style={{ padding: "10px 14px", color: Number(tradeNetProfitPct(t) || 0) >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{fmtPct(tradeNetProfitPct(t))}</td>
                        <td style={{ padding: "10px 14px", color: Number(t.stockMoveAtEntryPct ?? t.entryChangePct ?? 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtPct(t.stockMoveAtEntryPct ?? t.entryChangePct)}</td>
                        <td style={{ padding: "10px 14px", color: Number(t.stockMoveAtExitPct ?? t.exitChangePct ?? 0) >= 0 ? "#22c55e" : "#ef4444" }}>{fmtPct(t.stockMoveAtExitPct ?? t.exitChangePct)}</td>
                        <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 10 }}>
                          <div>{fmtPrice(t.initialStopLoss)}</div>
                          <div style={{ color: "#64748b" }}>{fmtPrice(t.stopLossAtExit)}</div>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 10 }}>
                          <div>{fmtPrice(t.takeProfitAtEntry)}</div>
                          <div style={{ color: "#64748b" }}>{fmtPrice(t.takeProfitAtExit)}</div>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#64748b" }}>{fmtDuration(t.holdingMs)}</td>
                        <td style={{ padding: "10px 14px" }}>Rs {Number(t.value ?? ((t.price || 0) * (t.qty || 0))).toFixed(0)}</td>
                        <td style={{ padding: "10px 14px", color: (t.grossPnl ?? t.pnl ?? 0) >= 0 ? "#22c55e" : "#ef4444" }}>{(t.grossPnl ?? t.pnl ?? 0) >= 0 ? "+" : ""}Rs {Number(t.grossPnl ?? t.pnl ?? 0).toFixed(2)}</td>
                        <td style={{ padding: "10px 14px", color: "#f59e0b" }}>Rs {Number(t.costs || 0).toFixed(2)}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Blacklist */}
          {tab === "blacklist" && (
            <div style={{ padding: isMobile ? 12 : 18, height: "100%", overflow: "auto", boxSizing: "border-box" }}>
              {blacklistSymbols.length === 0 ? (
                <div style={{ color: "#334155", fontSize: 12, textAlign: "center", padding: 30 }}>
                  No blacklisted shares.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
                  {blacklistSymbols.map((symbol) => (
                    <div key={symbol} style={{ background: "#07111f", border: "1px solid #1e293b", borderRadius: 8, padding: 12, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                      <div>
                        <div style={{ color: "#f87171", fontWeight: 900, fontSize: 13 }}>{symbol}</div>
                        <div style={{ color: "#64748b", fontSize: 10, marginTop: 3 }}>Blocked from auto/manual entry</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleBlacklist(symbol)}
                        style={{ background: "#1f1200", border: "1px solid #f59e0b66", color: "#f59e0b", borderRadius: 6, padding: "7px 9px", fontSize: 10, cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Funds */}
          {tab === "funds" && (
              <div style={{ padding: isMobile ? 12 : 20, height: "100%", overflow: "auto", boxSizing: "border-box" }}>
              {funds ? (
                <pre style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.8 }}>
                  {JSON.stringify(funds, null, 2)}
                </pre>
              ) : (
                <div style={{ color: "#334155", fontSize: 12, textAlign: "center", padding: 30 }}>No fund data available.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
